import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Task, Responsible, ProtocolConfig, ProtocolItem, PhysicalUnit, Level, ProtocolState, ChecklistStatus, ProtocolHistoryEntry, ChecklistValue, WeeklyPlan, ProjectInfo, CompanyConfig, GlobalUser, MasterProtocol } from '../src/types';

interface QualityScreenProps {
    tasks: Task[];
    responsibles: Responsible[];
    protocolConfigs: Record<string, ProtocolConfig>;
    setProtocolConfigs: React.Dispatch<React.SetStateAction<Record<string, ProtocolConfig>>>;
    units: PhysicalUnit[];
    levels: Level[];
    matrixData: Record<string, string>;
    weeklyPlans: WeeklyPlan[]; // New: for 1st reviewer linkage
    savedProtocols: Record<string, ProtocolState>;
    setSavedProtocols: React.Dispatch<React.SetStateAction<Record<string, ProtocolState>>>;
    projectInfo: ProjectInfo;
    addActivity: (activity: any) => void;
    companyConfig: CompanyConfig;
    hasPermission: (module: string, action: 'view'|'edit') => boolean;
    currentUser?: GlobalUser;
    globalProtocolTemplates: MasterProtocol[];
}

// Helper interface for the Master List
interface ProtocolInstance {
    id: string; // generated ID (e.g. PRT-001)
    taskName: string;
    taskId: string; // for reference
    taskIndex: string; // e.g. "1.1.3"
    status: 'Abierto' | 'Cerrado' | 'Inactivo' | 'Rechazado' | 'Resuelto';
    levelName: string;
    unitName: string;
    unitId: string;
    stage: string;
    date: string;
    protocolName: string;
    isSaved: boolean; // Flag to check if it exists in state
    isObsolete?: boolean;
}

const SignatureModal = ({ isOpen, onClose, onSave }: { isOpen: boolean, onClose: () => void, onSave: (data: string) => void }) => {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);

    React.useEffect(() => {
        if (!isOpen) return;
        // Small delay to ensure canvas is mounted and sized
        setTimeout(() => {
            const canvas = canvasRef.current;
            if (canvas) {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.lineWidth = 3;
                    ctx.lineCap = 'round';
                    ctx.lineJoin = 'round';
                    ctx.strokeStyle = '#1E3A8A';
                }
            }
        }, 50);
    }, [isOpen]);

    const getCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        if ('touches' in e) {
            return {
                x: e.touches[0].clientX - rect.left,
                y: e.touches[0].clientY - rect.top
            };
        }
        return {
            x: (e as React.MouseEvent).clientX - rect.left,
            y: (e as React.MouseEvent).clientY - rect.top
        };
    };

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        setIsDrawing(true);
        const coords = getCoordinates(e);
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx && coords) {
            ctx.lineWidth = 3;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.strokeStyle = '#1E3A8A';
            ctx.beginPath();
            ctx.moveTo(coords.x, coords.y);
            // Prevent scroll on touch devices
            if (e.cancelable) e.preventDefault();
        }
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;
        const coords = getCoordinates(e);
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx && coords) {
            ctx.lineTo(coords.x, coords.y);
            ctx.stroke();
            if (e.cancelable) e.preventDefault();
        }
    };

    const stopDrawing = () => {
        setIsDrawing(false);
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx) ctx.closePath();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-900 rounded-xl max-w-md w-full overflow-hidden shadow-2xl flex flex-col animate-fade-in-up">
                <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
                    <h3 className="font-bold text-slate-800 dark:text-white uppercase text-sm">Firma Electrónica</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                        <span className="material-symbols-outlined text-[20px]">close</span>
                    </button>
                </div>
                <div className="p-4 flex-1 flex flex-col items-center bg-slate-50 dark:bg-slate-800/50">
                    <p className="text-xs text-slate-500 mb-3 text-center">Dibuje su firma en el recuadro inferior para aprobar el protocolo.</p>
                    <div className="bg-white rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 overflow-hidden touch-none w-full max-w-[340px] aspect-[2/1]">
                        <canvas
                            ref={canvasRef}
                            width={340}
                            height={170}
                            className="w-full h-full cursor-crosshair bg-white"
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                            onTouchStart={startDrawing}
                            onTouchMove={draw}
                            onTouchEnd={stopDrawing}
                        />
                    </div>
                    <button 
                        onClick={() => {
                            const canvas = canvasRef.current;
                            if (canvas) {
                                const ctx = canvas.getContext('2d');
                                if (ctx) ctx.clearRect(0,0, canvas.width, canvas.height);
                            }
                        }}
                        className="mt-4 px-3 py-1.5 bg-slate-200 dark:bg-slate-700 rounded-full text-[10px] font-bold text-slate-600 dark:text-slate-300 hover:text-red-500 dark:hover:text-red-400 flex items-center gap-1 transition-colors"
                    >
                        <span className="material-symbols-outlined text-[14px]">ink_eraser</span> Limpiar Firma
                    </button>
                </div>
                <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">Cancelar</button>
                    <button 
                        onClick={() => {
                            if (canvasRef.current) {
                                onSave(canvasRef.current.toDataURL('image/png'));
                                onClose();
                            }
                        }} 
                        className="px-6 py-2 bg-primary hover:bg-blue-600 text-white text-xs font-bold rounded-lg shadow-md transition-colors"
                    >
                        Guardar Firma
                    </button>
                </div>
            </div>
        </div>
    );
};

const QualityScreen: React.FC<QualityScreenProps> = ({
    tasks,
    responsibles,
    protocolConfigs,
    setProtocolConfigs,
    units,
    levels,
    matrixData,
    weeklyPlans,
    savedProtocols,
    setSavedProtocols,
    projectInfo,
    addActivity,
    companyConfig,
    hasPermission,
    currentUser,
    globalProtocolTemplates
}) => {
    // --- State ---
    const location = useLocation();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [configModalOpen, setConfigModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedPartida, setSelectedPartida] = useState('Todas las Partidas');

    // Active Protocol Modal State (The "Detail/Paper" view)
    const [activeProtocolId, setActiveProtocolId] = useState<string | null>(null);
    const [protocolDetailModalOpen, setProtocolDetailModalOpen] = useState(false);
    const [tempChecklist, setTempChecklist] = useState<Record<string, { status: ChecklistStatus, comment: string, photos: string[] }>>({});
    const [currentSignature, setCurrentSignature] = useState<string | null>(null);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);

    // Rejection Logic
    const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
    const [rejectionReason, setRejectionReason] = useState("");
    const [rejectionPhotos, setRejectionPhotos] = useState<string[]>([]);

    // Resolution Logic (New)
    const [resolutionText, setResolutionText] = useState("");
    const [resolutionPhotos, setResolutionPhotos] = useState<string[]>([]);

    // History Logic
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [selectedSnapshotEntry, setSelectedSnapshotEntry] = useState<ProtocolHistoryEntry | null>(null);
    const [initialChecklist, setInitialChecklist] = useState<Record<string, { status: ChecklistStatus, comment: string, photos: string[] }>>({});

    // Config Modals State
    const [assignResponsiblesTask, setAssignResponsiblesTask] = useState<string | null>(null);
    const [tempResponsibleIds, setTempResponsibleIds] = useState<string[]>([]);
    const [baseConfigModalOpen, setBaseConfigModalOpen] = useState(false);
    const [activeBaseTaskId, setActiveBaseTaskId] = useState<string | null>(null);
    const [infoModalState, setInfoModalState] = useState<{ isOpen: boolean; itemId: string | null; text: string; isReadOnly?: boolean }>({ isOpen: false, itemId: null, text: '', isReadOnly: false });
    const [commentModalState, setCommentModalState] = useState<{ isOpen: boolean; itemId: string | null; text: string; isReadOnly?: boolean }>({ isOpen: false, itemId: null, text: '', isReadOnly: false });

    // Import from Master states
    const [importMasterModalOpen, setImportMasterModalOpen] = useState(false);
    const [activeImportTaskId, setActiveImportTaskId] = useState<string | null>(null);
    const [importSearch, setImportSearch] = useState('');
    const [selectedPreviewTemplate, setSelectedPreviewTemplate] = useState<MasterProtocol | null>(null);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [importModalTab, setImportModalTab] = useState<'maestro' | 'proyecto'>('maestro');

    // Photo Modal State (New)
    const [photoModalState, setPhotoModalState] = useState<{ isOpen: boolean; itemId: string | null; photos: string[]; isReadOnly?: boolean }>({ isOpen: false, itemId: null, photos: [], isReadOnly: false });

    const [reviewerGroupModalOpen, setReviewerGroupModalOpen] = useState(false);
    const [activeGroupTaskId, setActiveGroupTaskId] = useState<string | null>(null);
    const [unitSelectorOpen, setUnitSelectorOpen] = useState(false);
    const [activeGroupId, setActiveGroupId] = useState<string | null>(null);

    // Export Logic
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [selectedExportTaskId, setSelectedExportTaskId] = useState<string | null>(null);
    const [selectedProtocolIds, setSelectedProtocolIds] = useState<Set<string>>(new Set());

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 100;

    // Reset page on search or filter change
    const [mobileSelectedUnit, setMobileSelectedUnit] = useState<{ unitId: string, unitName: string, levelName: string } | null>(null);

    React.useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, selectedPartida]);

    // --- Role and Turn Validation Helpers ---
    const isProjectAdmin = (): boolean => {
        if (!currentUser) return false;
        if (currentUser.globalRole === 'Administrador') return true;
        const projectAssignment = currentUser.assignedProjects?.find(ap => ap.projectId === projectInfo.id);
        const profileName = projectAssignment?.profileName;
        return profileName === 'Administrador' || profileName === 'Administrador del Proyecto';
    };

    const isUserTurnExactly = (protocolId: string | null): boolean => {
        if (!protocolId) return false;
        if (isProjectAdmin()) return true;

        const savedState = savedProtocols[protocolId];
        const currentReviewerIndex = savedState ? (savedState.currentReviewerIndex || 0) : 0;

        const pData = protocolList.find(p => p.id === protocolId);
        if (!pData) return false;

        const config = protocolConfigs[pData.taskId];
        if (!config) return false;

        const group = config.reviewerGroups?.find(g => g.unitIds.includes(pData.unitId));
        if (!group || !group.reviewers || group.reviewers.length === 0) return false;

        const currentReviewer = group.reviewers[currentReviewerIndex];
        if (!currentReviewer) return false;

        const reviewerResponsible = responsibles.find(r => r.id === currentReviewer.id);
        return reviewerResponsible?.email === currentUser?.email;
    };

    const shouldShowProtocolToUser = (protocol: ProtocolInstance): boolean => {
        if (isProjectAdmin()) return true;

        const savedState = savedProtocols[protocol.id];
        const currentReviewerIndex = savedState ? (savedState.currentReviewerIndex || 0) : 0;

        const config = protocolConfigs[protocol.taskId];
        if (!config) return false;

        const group = config.reviewerGroups?.find(g => g.unitIds.includes(protocol.unitId));
        if (!group || !group.reviewers || group.reviewers.length === 0) return false;

        const userReviewerIndex = group.reviewers.findIndex(r => {
            const resp = responsibles.find(resp => resp.id === r.id);
            return resp?.email === currentUser?.email;
        });

        if (userReviewerIndex === -1) return false;

        return currentReviewerIndex >= userReviewerIndex;
    };

    const checkIsRevisadoPorMi = (item: ProtocolInstance): boolean => {
        if (item.status === 'Cerrado' || item.status === 'Rechazado' || item.status === 'Resuelto' || item.status === 'Inactivo') {
            return false;
        }
        if (!currentUser?.name) return false;
        const savedState = savedProtocols[item.id];
        if (!savedState || !savedState.history || savedState.history.length === 0) return false;
        for (let i = savedState.history.length - 1; i >= 0; i--) {
            const entry = savedState.history[i];
            if (entry.action === 'approved' || entry.action === 'resolved' || entry.action === 'closed') {
                return entry.user.includes(currentUser.name);
            }
        }
        return false;
    };

    // --- Helpers for Protocol List ---


    const getProtocolInstances = (): ProtocolInstance[] => {
        const instances: ProtocolInstance[] = [];
        let counter = 1;
        const activeTaskIds = new Set<string>();

        // Recursive function to traverse tasks
        const traverse = (taskList: Task[], indexPrefix: string) => {
            taskList.forEach((task, idx) => {
                activeTaskIds.add(task.id);
                const currentIndex = indexPrefix ? `${indexPrefix}.${idx + 1}` : `${idx + 1}`;

                if (task.type === 'task') {
                    const config = protocolConfigs[task.id];

                    if (config && config.applies) {
                        units.forEach(unit => {
                            const key = `quality_${task.id}_${unit.id}`;
                            const isSelected = matrixData[key] !== undefined && matrixData[key] !== null && matrixData[key] !== '' && matrixData[key] !== '0' && matrixData[key] !== '-';

                            if (isSelected) {
                                const level = levels.find(l => l.id === unit.levelId);
                                const codePrefix = config.code || 'PRT';
                                const formattedCounter = String(counter).padStart(3, '0');
                                const protocolId = `${codePrefix}-${formattedCounter}`;

                                // Check if this specific protocol has saved state
                                const savedState = savedProtocols[protocolId];

                                // Find relevant group for this unit
                                const group = config.reviewerGroups?.find(g => g.unitIds.includes(unit.id));
                                const totalReviewers = group?.reviewers.length || 1;

                                instances.push({
                                    id: protocolId,
                                    taskName: task.name,
                                    taskId: task.id,
                                    taskIndex: currentIndex,
                                    status: savedState ? savedState.status : 'Inactivo', // Default to Inactivo
                                    levelName: level?.name || '-',
                                    unitName: unit.name,
                                    unitId: unit.id,
                                    stage: savedState ? `Rev ${(savedState.currentReviewerIndex || 0) + 1}/${totalReviewers}` : `Rev 1/${totalReviewers}`,
                                    date: savedState ? savedState.creationDate : '-',
                                    protocolName: config.protocolName || 'Sin Nombre',
                                    isSaved: !!savedState
                                });
                                counter++;
                            }
                        });
                    }
                }

                if (task.children) {
                    traverse(task.children, currentIndex);
                }
            });
        };

        traverse(tasks, "");

        // Find saved protocols whose taskId is not in activeTaskIds (meaning the task was deleted or is outside Gantt)
        Object.keys(savedProtocols).forEach(protocolId => {
            const savedState = savedProtocols[protocolId];
            if (savedState && savedState.taskId && !activeTaskIds.has(savedState.taskId)) {
                // Find unit, level, config if possible
                const unit = units.find(u => u.id === savedState.unitId);
                const level = unit ? levels.find(l => l.id === unit.levelId) : null;
                const config = protocolConfigs[savedState.taskId];
                
                if (instances.some(i => i.id === protocolId)) return;

                // Fallback for task name
                let taskName = savedState.taskName || 'Partida Eliminada';
                if (!savedState.taskName && savedState.history && savedState.history.length > 0) {
                    const desc = savedState.history[0].comment || "";
                    const match = desc.match(/Protocolo\s+\S+\s+-\s+(.+?)\s*\(/);
                    if (match) {
                        taskName = match[1].trim();
                    }
                }
                if (taskName === 'Partida Eliminada' && config?.protocolName) {
                    taskName = config.protocolName;
                }

                instances.push({
                    id: protocolId,
                    taskName: taskName,
                    taskId: savedState.taskId,
                    taskIndex: 'N/A',
                    status: savedState.status,
                    levelName: level?.name || '-',
                    unitName: unit?.name || '-',
                    unitId: savedState.unitId,
                    stage: 'Histórico',
                    date: savedState.creationDate || '-',
                    protocolName: savedState.protocolName || config?.protocolName || 'Protocolo Obsoleto',
                    isSaved: true,
                    isObsolete: true
                });
            }
        });

        return instances;
    };

    const protocolList = useMemo<ProtocolInstance[]>(() => getProtocolInstances(), [tasks, protocolConfigs, units, levels, matrixData, savedProtocols]);

    const uniquePartidas = useMemo(() => {
        const names = Array.from(new Set(protocolList.map(p => p.taskName)));
        return names.sort();
    }, [protocolList]);

    const filteredProtocolList = useMemo(() => {
        return protocolList.filter(p => {
            const lowerSearch = searchQuery.toLowerCase();
            const searchRegex = searchQuery.trim() ? new RegExp(`\\b${searchQuery.trim().replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}\\b`, 'i') : null;

            const matchesSearch = p.id.toLowerCase().includes(lowerSearch) ||
                p.taskName.toLowerCase().includes(lowerSearch) ||
                (searchRegex && searchRegex.test(p.unitName)) ||
                (!searchRegex && p.unitName.toLowerCase().includes(lowerSearch)) ||
                p.taskIndex.includes(searchQuery);

            const matchesPartida = selectedPartida === 'Todas las Partidas' || p.taskName === selectedPartida;

            const matchesUserTurn = shouldShowProtocolToUser(p);

            return matchesSearch && matchesPartida && matchesUserTurn;
        });
    }, [protocolList, searchQuery, selectedPartida, savedProtocols, responsibles, currentUser, protocolConfigs, projectInfo]);

    const paginatedProtocols = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredProtocolList.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredProtocolList, currentPage]);

    const totalPages = Math.ceil(filteredProtocolList.length / ITEMS_PER_PAGE);

    // --- Actions for Protocol Execution ---

    const handleOpenProtocolDetail = (protocol: ProtocolInstance) => {
        setActiveProtocolId(protocol.id);
        setResolutionText(""); // Reset resolution text on open
        setResolutionPhotos([]); // Reset resolution photos on open

        // Initialize temp state from saved state OR defaults
        const savedState = savedProtocols[protocol.id];

        if (savedState) {
            // Load existing data
            const checklistMap: Record<string, { status: ChecklistStatus, comment: string, photos: string[] }> = {};
            // If the saved state checklist structure maps directly
            Object.values(savedState.checklist).forEach((v: ChecklistValue) => {
                checklistMap[v.itemId] = {
                    status: v.status,
                    comment: v.comment || '',
                    photos: v.photos || []
                };
            });
            setTempChecklist(checklistMap);
            setInitialChecklist(JSON.parse(JSON.stringify(checklistMap))); // Capture initial checklist
            // Current signature for THIS revision step is typically empty unless already signed but not sent
            setCurrentSignature(savedState.signatures[savedState.currentReviewerIndex] || null);
        } else {
            // New Protocol - Initialize clean
            setTempChecklist({});
            setInitialChecklist({});
            setCurrentSignature(null);
        }

        setProtocolDetailModalOpen(true);
    };

    const handleCloseProtocolModal = () => {
        setProtocolDetailModalOpen(false);
        if (location.state?.fromPlanning) {
            navigate(-1);
        }
    };

    // Handle Auto-Open from Planning
    useEffect(() => {
        if (location.state?.openProtocolTaskId && location.state?.openProtocolUnitId && protocolList.length > 0) {
            const found = protocolList.find(p => p.taskId === location.state.openProtocolTaskId && p.unitId === location.state.openProtocolUnitId);
            if (found && !protocolDetailModalOpen) {
                handleOpenProtocolDetail(found);
                
                // Clear state so it doesn't reopen if they close and do something else
                window.history.replaceState({}, document.title);
            }
        }
    }, [location.state, protocolList]);

    const handleChecklistClick = (itemId: string, status: ChecklistStatus) => {
        setTempChecklist(prev => ({
            ...prev,
            [itemId]: {
                ...(prev[itemId] || { comment: '', photos: [] }),
                status: status
            }
        }));
    };

    const handleCommentClick = (itemId: string) => {
        const isReadOnly = !isUserTurnExactly(activeProtocolId) || getCurrentState().status === 'Cerrado';
        
        // Extract the new comment part. If current comment starts with the inherited comment, the new part is the rest.
        const inheritedComment = initialChecklist[itemId]?.comment || '';
        const currentComment = tempChecklist[itemId]?.comment || '';
        let newCommentPart = '';
        
        if (inheritedComment && currentComment.startsWith(inheritedComment)) {
            newCommentPart = currentComment.substring(inheritedComment.length).replace(/^\n\[.*?\]:\s*/, '');
        } else if (!inheritedComment) {
            newCommentPart = currentComment;
        }

        setCommentModalState({
            isOpen: true,
            itemId,
            text: newCommentPart,
            isReadOnly: isReadOnly
        });
    };

    const handlePhotoClick = (itemId: string) => {
        const isReadOnly = !isUserTurnExactly(activeProtocolId) || getCurrentState().status === 'Cerrado';
        setPhotoModalState({
            isOpen: true,
            itemId,
            photos: tempChecklist[itemId]?.photos || [],
            isReadOnly: isReadOnly
        });
    };

    const handleSaveComment = () => {
        if (commentModalState.itemId) {
            const itemId = commentModalState.itemId;
            const inheritedComment = initialChecklist[itemId]?.comment || '';
            const newCommentPart = commentModalState.text.trim();
            const config = getActiveConfig();
            const pData = getActiveProtocolData();
            const currentReviewerName = getReviewerName(getCurrentState().currentReviewerIndex, config, pData?.unitId);

            let finalComment = inheritedComment;
            if (newCommentPart) {
                if (inheritedComment) {
                    finalComment = `${inheritedComment}\n[${currentReviewerName}]: ${newCommentPart}`;
                } else {
                    finalComment = newCommentPart;
                }
            }

            setTempChecklist(prev => ({
                ...prev,
                [itemId]: {
                    ...(prev[itemId] || { status: 'pending', photos: [] }),
                    comment: finalComment
                }
            }));
        }
        setCommentModalState({ isOpen: false, itemId: null, text: '' });
    };

    const handleAddPhoto = () => {
        document.getElementById('cameraInput')?.click();
    };

    const handleRemovePhoto = (index: number) => {
        if (photoModalState.itemId) {
            const itemId = photoModalState.itemId;
            const inheritedPhotos = initialChecklist[itemId]?.photos || [];
            const photoToRemove = photoModalState.photos[index];

            if (inheritedPhotos.includes(photoToRemove)) {
                alert("No puede eliminar fotografías de revisiones anteriores.");
                return;
            }

            const updatedPhotos = photoModalState.photos.filter((_, i) => i !== index);

            setPhotoModalState({ ...photoModalState, photos: updatedPhotos });

            setTempChecklist(prev => ({
                ...prev,
                [itemId]: {
                    ...(prev[itemId] || { status: 'pending', comment: '' }),
                    photos: updatedPhotos
                }
            }));
        }
    };

    const handleSign = () => {
        setSignatureModalOpen(true);
    };

    // --- Save / Send / Reject Logic ---

    const getActiveProtocolData = () => {
        if (!activeProtocolId) return null;
        return protocolList.find(p => p.id === activeProtocolId);
    };

    const getActiveConfig = () => {
        const pData = getActiveProtocolData();
        if (!pData) return null;
        return protocolConfigs[pData.taskId];
    };

    const getCurrentState = (): ProtocolState => {
        if (!activeProtocolId) throw new Error("No active protocol");
        const pData = getActiveProtocolData();
        const config = getActiveConfig();
        const group = config?.reviewerGroups?.find(g => g.unitIds.includes(pData?.unitId || ''));

        return savedProtocols[activeProtocolId] || {
            id: activeProtocolId,
            taskId: pData?.taskId || '',
            unitId: pData?.unitId || '',
            groupId: group?.id || '',
            status: 'Abierto',
            currentReviewerIndex: 0,
            checklist: {},
            signatures: {},
            history: [],
            creationDate: new Date().toLocaleDateString('es-CL'),
            taskName: pData?.taskName || '',
            protocolName: config?.protocolName || ''
        };
    };

    const handleSaveProtocol = () => {
        if (!activeProtocolId) return;
        const currentState = getCurrentState();

        // Construct checklist map
        const checklistToSave: Record<string, any> = {};
        Object.keys(tempChecklist).forEach(key => {
            checklistToSave[key] = { itemId: key, ...tempChecklist[key] };
        });

        // Update signatures
        const updatedSignatures = { ...currentState.signatures };
        if (currentSignature) {
            updatedSignatures[currentState.currentReviewerIndex] = currentSignature;
        }

        const config = getActiveConfig();
        const pData = getActiveProtocolData();
        const group = config?.reviewerGroups?.find(g => g.unitIds.includes(pData?.unitId || ''));

        const newState: ProtocolState = {
            ...currentState,
            groupId: group?.id || currentState.groupId || '',
            checklist: checklistToSave,
            signatures: updatedSignatures,
            status: currentState.status === 'Inactivo' ? 'Abierto' : currentState.status,
            taskName: pData?.taskName || currentState.taskName,
            protocolName: config?.protocolName || currentState.protocolName
        };

        // Add history entry if it's new
        if (!savedProtocols[activeProtocolId]) {
            newState.history.push({
                date: new Date().toLocaleString(),
                action: 'created',
                user: 'Sistema', // Or current user
                snapshot: JSON.parse(JSON.stringify(checklistToSave))
            });
        }
        const isFirstSave = !savedProtocols[activeProtocolId];

        setSavedProtocols(prev => ({ ...prev, [activeProtocolId]: newState }));

        if (isFirstSave) {
            addActivity({
                type: 'quality',
                title: 'Nuevo Protocolo Habilitado',
                description: `Se ha habilitado el protocolo ${activeProtocolId} para la tarea ${pData?.taskName || 'desconocida'}`,
                user: 'Usuario'
            });
        }

        handleCloseProtocolModal();
    };

    const handleSendProtocol = () => {
        if (!activeProtocolId) return;

        const currentState = getCurrentState();
        const config = getActiveConfig();
        const items = config?.items || [];

        const errors: string[] = [];

        // 1. Campo de control items validation
        items.forEach(item => {
            const itemState = tempChecklist[item.id];
            const isAnswered = itemState && (itemState.status === 'pass' || itemState.status === 'fail' || itemState.status === 'na');
            if (!isAnswered) {
                errors.push(`Falta completar el parámetro ${item.text}`);
            }
        });

        // 2. Fotografía validation
        const hasPhoto = items.some(item => tempChecklist[item.id]?.photos && tempChecklist[item.id].photos.length > 0);
        if (!hasPhoto) {
            errors.push("Falta Adjuntar Fotografía");
        }

        // 3. Firma validation
        if (!currentSignature) {
            errors.push("Falta firmar el protocolo");
        }

        // 4. Resolution validation if rejected
        if (currentState.status === 'Rechazado') {
            if (!resolutionText.trim()) {
                errors.push("Debe ingresar la resolución de la observación antes de enviar");
            }
            if (resolutionPhotos.length === 0) {
                errors.push("Debe adjuntar al menos una fotografía de la reparación");
            }
        }

        if (errors.length > 0) {
            if (errors.length === 1) {
                alert(errors[0]);
            } else {
                alert(`Para enviar el protocolo es necesario solucionar lo siguiente:\n${errors.map(err => ` - ${err}`).join('\n')}`);
            }
            return;
        }

        // getActiveConfig already called above
        const pData = getActiveProtocolData();
        const group = config?.reviewerGroups?.find(g => g.unitIds.includes(pData?.unitId || ''));
        const totalReviewers = group?.reviewers.length || 1;

        const checklistToSave: Record<string, any> = {};
        Object.keys(tempChecklist).forEach(key => {
            checklistToSave[key] = { itemId: key, ...tempChecklist[key] };
        });

        const updatedSignatures = { ...currentState.signatures };
        updatedSignatures[currentState.currentReviewerIndex] = currentSignature;

        const isLastReviewer = currentState.currentReviewerIndex >= totalReviewers - 1;

        const newState: ProtocolState = {
            ...currentState,
            groupId: group?.id || currentState.groupId || '',
            checklist: checklistToSave,
            signatures: updatedSignatures,
            currentReviewerIndex: isLastReviewer ? currentState.currentReviewerIndex : currentState.currentReviewerIndex + 1,
            status: isLastReviewer ? 'Cerrado' : (currentState.status === 'Rechazado' ? 'Resuelto' : 'Abierto'),
            taskName: pData?.taskName || currentState.taskName,
            protocolName: config?.protocolName || currentState.protocolName
        };

        // If resolving a rejection, add a resolution history entry
        if (currentState.status === 'Rechazado') {
            newState.history.push({
                date: new Date().toLocaleString(),
                action: 'resolved',
                user: getReviewerName(currentState.currentReviewerIndex, config, pData?.unitId),
                comment: `Resolución: ${resolutionText}`,
                images: resolutionPhotos,
                snapshot: JSON.parse(JSON.stringify(checklistToSave))
            });
        }

        newState.history.push({
            date: new Date().toLocaleString(),
            action: isLastReviewer ? 'closed' : 'approved',
            user: getReviewerName(currentState.currentReviewerIndex, config, pData?.unitId),
            comment: isLastReviewer ? 'Protocolo Cerrado' : 'Enviado a siguiente revisor',
            snapshot: JSON.parse(JSON.stringify(checklistToSave))
        });

        const isClosing = isLastReviewer;
        const pDataNormal = getProtocolInstances().find(p => p.id === activeProtocolId);

        setSavedProtocols(prev => ({ ...prev, [activeProtocolId]: newState }));

        // Log Activity
        addActivity({
            type: 'quality',
            title: isClosing ? 'Protocolo Cerrado' : 'Protocolo Enviado a Revisión',
            description: `Protocolo ${activeProtocolId} - ${pDataNormal?.taskName} (${pDataNormal?.unitName})`,
            user: 'Usuario'
        });

        handleCloseProtocolModal();
    };

    const handleRejectClick = () => {
        setRejectionReason("");
        setRejectionPhotos([]);
        setRejectionModalOpen(true);
    };

    const confirmRejection = () => {
        if (!activeProtocolId) return;
        const currentState = getCurrentState();
        const config = getActiveConfig();

        // Must be at least reviewer 2 (index 1) to reject to previous
        if (currentState.currentReviewerIndex === 0) return;

        const pData = getActiveProtocolData();
        const newState: ProtocolState = {
            ...currentState,
            currentReviewerIndex: currentState.currentReviewerIndex - 1,
            status: 'Rechazado',
            taskName: pData?.taskName || currentState.taskName,
            protocolName: config?.protocolName || currentState.protocolName
        };

        newState.history.push({
            date: new Date().toLocaleString(),
            action: 'rejected',
            user: getReviewerName(currentState.currentReviewerIndex, config, currentState.unitId),
            comment: rejectionReason,
            images: rejectionPhotos,
            snapshot: JSON.parse(JSON.stringify(tempChecklist))
        });

        const pDataNormal = getProtocolInstances().find(p => p.id === activeProtocolId);

        setSavedProtocols(prev => ({ ...prev, [activeProtocolId]: newState }));

        // Log Activity
        addActivity({
            type: 'quality',
            title: 'Protocolo Rechazado',
            description: `Protocolo ${activeProtocolId} - ${pDataNormal?.taskName}. Razón: ${rejectionReason.substring(0, 30)}...`,
            user: 'Usuario'
        });

        setRejectionModalOpen(false);
        handleCloseProtocolModal();
    };

    const handleAdminLiberate = () => {
        if (!activeProtocolId || !currentSignature) {
            alert("Debe firmar el protocolo antes de liberar.");
            return;
        }

        const currentState = getCurrentState();
        const config = getActiveConfig();
        const pData = getActiveProtocolData();
        const group = config?.reviewerGroups?.find(g => g.unitIds.includes(pData?.unitId || ''));

        const checklistToSave: Record<string, any> = {};
        Object.keys(tempChecklist).forEach(key => {
            checklistToSave[key] = { itemId: key, ...tempChecklist[key] };
        });

        const updatedSignatures = { ...currentState.signatures };
        updatedSignatures[currentState.currentReviewerIndex] = currentSignature;

        const newState: ProtocolState = {
            ...currentState,
            groupId: group?.id || currentState.groupId || '',
            checklist: checklistToSave,
            signatures: updatedSignatures,
            status: 'Cerrado',
            taskName: pData?.taskName || currentState.taskName,
            protocolName: config?.protocolName || currentState.protocolName
        };

        newState.history.push({
            date: new Date().toLocaleString(),
            action: 'closed',
            user: `${currentUser?.name || 'Administrador'} (Admin del Proyecto)`,
            comment: 'Protocolo Liberado directamente por Administrador',
            snapshot: JSON.parse(JSON.stringify(checklistToSave))
        });

        const pDataNormal = getProtocolInstances().find(p => p.id === activeProtocolId);

        setSavedProtocols(prev => ({ ...prev, [activeProtocolId]: newState }));

        addActivity({
            type: 'quality',
            title: 'Protocolo Liberado (Admin)',
            description: `Protocolo ${activeProtocolId} - ${pDataNormal?.taskName} (${pDataNormal?.unitName})`,
            user: currentUser?.name || 'Administrador'
        });

        handleCloseProtocolModal();
    };

    // Helper to get reviewer name from config
    const getReviewerName = (index: number, config: ProtocolConfig | null | undefined, unitId?: string) => {
        const actualUnitId = unitId || (activeProtocolId ? savedProtocols[activeProtocolId]?.unitId : undefined) || getActiveProtocolData()?.unitId;
        if (!config || !actualUnitId) return `Revisor ${index + 1}`;
        const group = config.reviewerGroups?.find(g => g.unitIds.includes(actualUnitId));
        if (!group) return `Revisor ${index + 1}`;

        const reviewer = group.reviewers[index];
        return reviewer ? `Revisor ${index + 1} (${reviewer.name})` : `Revisor ${index + 1}`;
    };

    // Helper to get last rejection comment
    const getLastRejectionReason = (history: ProtocolHistoryEntry[]) => {
        // Search backwards
        for (let i = history.length - 1; i >= 0; i--) {
            if (history[i].action === 'rejected') {
                return history[i].comment || "Sin motivo especificado";
            }
        }
        return "No se encontró el motivo del rechazo";
    };

    // Helper to get the complete last rejection entry
    const getLastRejectionEntry = (history: ProtocolHistoryEntry[]) => {
        for (let i = history.length - 1; i >= 0; i--) {
            if (history[i].action === 'rejected') {
                return history[i];
            }
        }
        return null;
    };

    // --- Existing Handlers (Config, Base, etc.) ---
    const handleConfigChange = (taskId: string, field: keyof ProtocolConfig, value: any) => {
        setProtocolConfigs(prev => {
            const currentConfig = prev[taskId] || { taskId, applies: false, reviewerGroups: [], code: '', protocolName: '', items: [] };
            let newConfig = { ...currentConfig, [field]: value };
            return { ...prev, [taskId]: newConfig };
        });
    };

    const handleOpenReviewerGroups = (taskId: string) => {
        setActiveGroupTaskId(taskId);
        setReviewerGroupModalOpen(true);
    };

    const handleAddGroup = () => {
        if (!activeGroupTaskId) return;
        const newGroup = {
            id: Math.random().toString(36).substr(2, 9),
            name: '',
            unitIds: [],
            reviewers: [
                { id: '', name: '', generatesProgress: false, generatesPayment: false }
            ]
        };
        handleConfigChange(activeGroupTaskId, 'reviewerGroups', [...(protocolConfigs[activeGroupTaskId]?.reviewerGroups || []), newGroup]);
    };

    const handleUpdateGroup = (groupId: string, field: string, value: any) => {
        if (!activeGroupTaskId) return;
        const groups = protocolConfigs[activeGroupTaskId]?.reviewerGroups?.map(g =>
            g.id === groupId ? { ...g, [field]: value } : g
        ) || [];
        handleConfigChange(activeGroupTaskId, 'reviewerGroups', groups);
    };

    const handleAddReviewerToGroup = (groupId: string) => {
        if (!activeGroupTaskId) return;
        const groups = protocolConfigs[activeGroupTaskId]?.reviewerGroups?.map(g => {
            if (g.id === groupId) {
                // If it's the very first reviewer being added (shouldn't happen with updated handleAddGroup, but just in case)
                if (g.reviewers.length === 0) {
                    return { ...g, reviewers: [{ id: '', name: '', generatesProgress: false, generatesPayment: false }] };
                }
                const newReviewer = { id: '', name: '', generatesProgress: false, generatesPayment: false };
                return { ...g, reviewers: [...g.reviewers, newReviewer] };
            }
            return g;
        }) || [];
        handleConfigChange(activeGroupTaskId, 'reviewerGroups', groups);
    };

    const handleUpdateReviewerInGroup = (groupId: string, reviewerIndex: number, field: string, value: any) => {
        if (!activeGroupTaskId) return;
        const groups = protocolConfigs[activeGroupTaskId]?.reviewerGroups?.map(g => {
            if (g.id === groupId) {
                const newReviewers = [...g.reviewers];
                newReviewers[reviewerIndex] = { ...newReviewers[reviewerIndex], [field]: value };
                // If ID is updated, also update name from responsibles
                if (field === 'id') {
                    const resp = responsibles.find(r => r.id === value);
                    newReviewers[reviewerIndex].name = resp ? resp.name : '';
                }
                return { ...g, reviewers: newReviewers };
            }
            return g;
        }) || [];
        handleConfigChange(activeGroupTaskId, 'reviewerGroups', groups);
    };

    const handleRemoveGroup = (groupId: string) => {
        if (!activeGroupTaskId) return;
        const groups = protocolConfigs[activeGroupTaskId]?.reviewerGroups?.filter(g => g.id !== groupId) || [];
        handleConfigChange(activeGroupTaskId, 'reviewerGroups', groups);
    };


    const handleSaveResponsibles = () => {
        if (!assignResponsiblesTask) return;
        setProtocolConfigs(prev => ({ ...prev, [assignResponsiblesTask]: { ...prev[assignResponsiblesTask], responsibleIds: tempResponsibleIds } }));
        setAssignResponsiblesTask(null);
    };

    const handleTempResponsibleChange = (index: number, value: string) => {
        const newIds = [...tempResponsibleIds];
        newIds[index] = value;
        setTempResponsibleIds(newIds);
    };

    const handleOpenBaseConfig = (taskId: string) => {
        setActiveBaseTaskId(taskId);
        setBaseConfigModalOpen(true);
    };

    const handleAddBaseItem = () => {
        if (!activeBaseTaskId) return;
        setProtocolConfigs(prev => {
            const config = prev[activeBaseTaskId];
            if (!config) return prev;

            const currentItems = config.items || [];
            const newItem: ProtocolItem = { id: Math.random().toString(36).substr(2, 9), text: '', additionalInfo: '' };
            return { ...prev, [activeBaseTaskId]: { ...config, items: [...currentItems, newItem] } };
        });
    };

    const handleUpdateBaseItem = (itemId: string, text: string) => {
        if (!activeBaseTaskId) return;
        setProtocolConfigs(prev => {
            const config = prev[activeBaseTaskId];
            if (!config) return prev;
            const updatedItems = config.items?.map(item => item.id === itemId ? { ...item, text } : item) || [];
            return { ...prev, [activeBaseTaskId]: { ...config, items: updatedItems } };
        });
    };

    const handleOpenInfoModal = (itemId: string, readOnly = false) => {
        // If coming from Detail View, we need to find the item in active config
        let text = '';
        if (readOnly && activeProtocolId) {
            const pData = getActiveProtocolData();
            const config = protocolConfigs[pData?.taskId || ''];
            const item = config?.items?.find(i => i.id === itemId);
            text = item?.additionalInfo || '';
        } else if (activeBaseTaskId) {
            // Config mode
            const config = protocolConfigs[activeBaseTaskId];
            const item = config?.items?.find(i => i.id === itemId);
            text = item?.additionalInfo || '';
        }
        setInfoModalState({ isOpen: true, itemId, text, isReadOnly: readOnly });
    };

    const handleSaveInfo = () => {
        if (infoModalState.isReadOnly) {
            setInfoModalState({ isOpen: false, itemId: null, text: '', isReadOnly: false });
            return;
        }

        if (!activeBaseTaskId || !infoModalState.itemId) return;
        setProtocolConfigs(prev => {
            const config = prev[activeBaseTaskId];
            if (!config) return prev;
            const updatedItems = config.items?.map(item => item.id === infoModalState.itemId ? { ...item, additionalInfo: infoModalState.text } : item) || [];
            return { ...prev, [activeBaseTaskId]: { ...config, items: updatedItems } };
        });
        setInfoModalState({ isOpen: false, itemId: null, text: '', isReadOnly: false });
    };

    const handleDeleteBaseItem = (itemId: string) => {
        if (!activeBaseTaskId) return;
        setProtocolConfigs(prev => {
            const config = prev[activeBaseTaskId];
            if (!config) return prev;
            const updatedItems = config.items?.filter(item => item.id !== itemId) || [];
            return { ...prev, [activeBaseTaskId]: { ...config, items: updatedItems } };


        });
    };

    const handleImportFromMaster = (template: MasterProtocol) => {
        if (!activeImportTaskId) return;

        setProtocolConfigs(prev => {
            const existingConfig = prev[activeImportTaskId] || { taskId: activeImportTaskId, applies: true };
            
            const importedItems: ProtocolItem[] = (template.items || []).map(item => ({
                id: Math.random().toString(36).substr(2, 9),
                text: item.text,
                additionalInfo: item.additionalInfo || ''
            }));

            return {
                ...prev,
                [activeImportTaskId]: {
                    ...existingConfig,
                    applies: true,
                    protocolName: template.protocolName,
                    code: template.code || '',
                    category: template.category,
                    subcategory: template.subcategory,
                    items: importedItems
                }
            };
        });

        setImportMasterModalOpen(false);
        setActiveImportTaskId(null);
    };

    const handleDuplicateProjectProtocol = (sourceConfig: ProtocolConfig) => {
        if (!activeImportTaskId) return;

        // Deep clone items
        const clonedItems = (sourceConfig.items || []).map(item => ({
            id: Math.random().toString(36).substr(2, 9),
            text: item.text,
            additionalInfo: item.additionalInfo || ''
        }));

        // Deep clone reviewer groups
        const clonedReviewerGroups = (sourceConfig.reviewerGroups || []).map(group => ({
            id: Math.random().toString(36).substr(2, 9),
            name: group.name,
            unitIds: [...(group.unitIds || [])],
            reviewers: (group.reviewers || []).map(r => ({
                id: r.id,
                name: r.name,
                generatesProgress: r.generatesProgress,
                generatesPayment: r.generatesPayment
            }))
        }));

        setProtocolConfigs(prev => {
            const existingConfig = prev[activeImportTaskId] || { taskId: activeImportTaskId, applies: true };
            return {
                ...prev,
                [activeImportTaskId]: {
                    ...existingConfig,
                    applies: true,
                    protocolName: sourceConfig.protocolName,
                    code: sourceConfig.code || '',
                    category: sourceConfig.category,
                    subcategory: sourceConfig.subcategory,
                    items: clonedItems,
                    reviewerGroups: clonedReviewerGroups
                }
            };
        });

        setImportMasterModalOpen(false);
        setActiveImportTaskId(null);
    };

    const getProjectProtocols = () => {
        const list: { task: Task; config: ProtocolConfig }[] = [];
        const traverse = (taskList: Task[]) => {
            taskList.forEach(task => {
                const config = protocolConfigs[task.id];
                if (config && config.applies && (config.protocolName || (config.items && config.items.length > 0))) {
                    list.push({ task, config });
                }
                if (task.children) {
                    traverse(task.children);
                }
            });
        };
        traverse(tasks);
        return list;
    };

    // Render configuration rows (Master Table)
    const renderConfigRows = (taskList: Task[], indexPrefix = ""): React.ReactNode[] => {
        let rows: React.ReactNode[] = [];
        taskList.forEach((task, index) => {
            const currentIndex = indexPrefix ? `${indexPrefix}.${index + 1}` : `${index + 1}`;
            const config = protocolConfigs[task.id] || { taskId: task.id, applies: false, reviewers: 0, responsibleIds: [], code: '', protocolName: '', reviewerGroups: [] };
            const isFolder = task.type === 'folder';

            rows.push(
                <tr key={task.id} className={`border-b border-slate-100 dark:border-slate-700 ${isFolder ? 'bg-slate-100 dark:bg-slate-800' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                    <td className="px-4 py-3 text-sm text-slate-500 font-mono align-middle">{currentIndex}</td>
                    <td className="px-4 py-3 text-sm text-slate-800 dark:text-slate-200 align-middle">
                        <span style={{ paddingLeft: `${(currentIndex.split('.').length - 1) * 12}px` }} className={isFolder ? 'font-bold' : 'font-medium'}>
                            {task.name}
                        </span>
                    </td>
                    {isFolder ? (
                        <>
                            <td className="px-4 py-3 text-center align-middle"><span className="text-slate-300">-</span></td>
                            <td className="px-4 py-3 text-center align-middle"><span className="text-slate-300">-</span></td>
                            <td className="px-4 py-3 text-center align-middle"><span className="text-slate-300">-</span></td>
                            <td className="px-4 py-3 text-center align-middle"><span className="text-slate-300">-</span></td>
                            <td className="px-4 py-3 text-center align-middle"><span className="text-slate-300">-</span></td>
                            <td className="px-4 py-3 text-center align-middle"><span className="text-slate-300">-</span></td>
                            <td className="px-4 py-3 text-center align-middle"><span className="text-slate-300">-</span></td>
                        </>
                    ) : (
                        <>
                            <td className="px-4 py-3 text-center align-middle">
                                <select
                                    className={`border rounded px-2 py-1 text-xs font-medium outline-none ${config.applies ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}
                                    value={config.applies ? 'yes' : 'no'}
                                    onChange={(e) => handleConfigChange(task.id, 'applies', e.target.value === 'yes')}
                                >
                                    <option value="no">No</option>
                                    <option value="yes">Si</option>
                                </select>
                            </td>
                            <td className="px-4 py-3 text-center align-middle">
                                <input
                                    type="text"
                                    className="w-full min-w-[100px] border border-slate-300 dark:border-slate-600 rounded px-2 py-1 text-sm bg-white dark:bg-slate-800 disabled:opacity-30 disabled:bg-slate-100 dark:disabled:bg-slate-800 outline-none focus:border-primary"
                                    placeholder="Ej: PRT-001"
                                    disabled={!config.applies}
                                    value={config.code || ''}
                                    onChange={(e) => handleConfigChange(task.id, 'code', e.target.value)}
                                />
                            </td>
                            <td className="px-4 py-3 text-center align-middle">
                                <input
                                    type="text"
                                    className="w-full min-w-[160px] border border-slate-300 dark:border-slate-600 rounded px-2 py-1 text-sm bg-white dark:bg-slate-800 disabled:opacity-30 disabled:bg-slate-100 dark:disabled:bg-slate-800 outline-none focus:border-primary"
                                    placeholder="Nombre del protocolo..."
                                    disabled={!config.applies}
                                    value={config.protocolName || ''}
                                    onChange={(e) => handleConfigChange(task.id, 'protocolName', e.target.value)}
                                />
                            </td>
                            <td className="px-4 py-3 text-center align-middle">
                                <button
                                    onClick={() => handleOpenReviewerGroups(task.id)}
                                    disabled={!config.applies}
                                    className={`
                                        flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium w-full mx-auto max-w-[140px] transition-colors
                                        ${!config.applies
                                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed dark:bg-slate-800 dark:text-slate-600'
                                            : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700'}
                                    `}
                                >
                                    <span className="material-symbols-outlined text-[16px]">
                                        {(config.reviewerGroups?.length || 0) > 0 ? 'group' : 'person_add'}
                                    </span>
                                    <span>
                                        {config.reviewerGroups?.length || 0} Grupos
                                    </span>
                                </button>
                            </td>
                            <td className="px-4 py-3 text-center align-middle">
                                <button
                                    onClick={() => handleOpenBaseConfig(task.id)}
                                    className={`p-1.5 rounded transition-colors ${config.applies ? 'text-slate-500 hover:text-primary hover:bg-slate-100' : 'text-slate-200 cursor-not-allowed'}`}
                                    disabled={!config.applies}
                                    title="Configurar Plantilla Base"
                                >
                                    <span className="material-symbols-outlined text-[20px]">description</span>
                                </button>
                            </td>
                            <td className="px-4 py-3 text-center align-middle">
                                <button
                                    onClick={() => {
                                        setActiveImportTaskId(task.id);
                                        setImportMasterModalOpen(true);
                                        setImportSearch('');
                                        setImportModalTab('maestro');
                                    }}
                                    className={`p-1.5 rounded transition-colors ${config.applies ? 'text-slate-500 hover:text-primary hover:bg-slate-100' : 'text-slate-200 cursor-not-allowed'}`}
                                    disabled={!config.applies}
                                    title="Biblioteca de Protocolos"
                                >
                                    <span className="material-symbols-outlined text-[20px]">library_books</span>
                                </button>
                            </td>
                        </>
                    )}
                </tr>
            );

            if (task.children) {
                rows = [...rows, ...renderConfigRows(task.children, currentIndex)];
            }
        });
        return rows;
    };

    // Calculate Active Base Config for Modal
    const activeBaseConfig = activeBaseTaskId ? protocolConfigs[activeBaseTaskId] : null;

    const normalizeTaskName = (name: string): string => {
        let clean = name.replace(/^\[[\d\.]+\]\s*/, '');
        return clean
            .trim()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, ""); // Remove accents
    };

    const findTaskNameRecursive = (list: Task[], id: string): string => {
        for (const t of list) {
            if (t.id === id) return t.name;
            if (t.children) {
                const found = findTaskNameRecursive(t.children, id);
                if (found) return found;
            }
        }
        return "";
    };

    const activeBaseTaskName = activeBaseTaskId ? findTaskNameRecursive(tasks, activeBaseTaskId) : '';

    const tasksWithProtocols = useMemo(() => {
        const result: { id: string, name: string }[] = [];
        const traverse = (list: Task[]) => {
            list.forEach(t => {
                if (t.type === 'task' && protocolConfigs[t.id]?.applies) {
                    result.push({ id: t.id, name: t.name });
                }
                if (t.children) traverse(t.children);
            });
        };
        traverse(tasks);
        return result;
    }, [tasks, protocolConfigs]);

    const protocolsForExport = useMemo(() => {
        if (!selectedExportTaskId) return [];
        return protocolList.filter(p => p.taskId === selectedExportTaskId && p.isSaved);
    }, [selectedExportTaskId, protocolList]);

    // Helper to load image asynchronously (Base64 or URL)
    const loadImage = (url: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = (e) => reject(new Error(`Failed to load image: ${url}`));
            img.src = url;
        });
    };

    // Helper to get reviewer role from responsibles list
    const getReviewerRole = (reviewerId: string) => {
        const resp = responsibles.find(r => r.id === reviewerId);
        return resp ? resp.role : "Revisor";
    };

    // Helper to find and format signature date from history
    const getSignatureDate = (idx: number, reviewerName: string, history: ProtocolHistoryEntry[]) => {
        const reviewerCleanName = reviewerName.replace(/Revisor \d+ \((.*?)\)/, '$1').trim();
        const entry = history.find(h => {
            const isApproveOrClose = h.action === 'approved' || h.action === 'closed' || h.action === 'resolved';
            if (!isApproveOrClose) return false;
            return h.user.includes(reviewerCleanName);
        });
        if (entry) {
            try {
                const dateObj = new Date(entry.date);
                if (!isNaN(dateObj.getTime())) {
                    const day = String(dateObj.getDate()).padStart(2, '0');
                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const year = dateObj.getFullYear();
                    const hours = String(dateObj.getHours()).padStart(2, '0');
                    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
                    return `${day}-${month}-${year}, ${hours}:${minutes} hrs`;
                }
            } catch (e) {
                // Ignore parse errors, return raw date
            }
            return entry.date;
        }
        return new Date().toLocaleDateString() + ", " + new Date().toLocaleTimeString().substring(0, 5) + " hrs";
    };

    const handleExportSelected = async () => {
        if (selectedProtocolIds.size === 0) {
            alert("Seleccione al menos un protocolo para exportar.");
            return;
        }

        const doc = new jsPDF();
        
        const drawWatermark = () => {
            doc.setTextColor(248, 250, 252);
            doc.setFontSize(45);
            doc.setFont("helvetica", "bold");
            doc.text("K-LYD SYSTEM", 105, 150, { align: 'center', angle: 45 });
            doc.setTextColor(30, 41, 59); // Reset color
        };

        let firstPage = true;

        const protocolIdsArray = Array.from(selectedProtocolIds);
        for (const protocolId of protocolIdsArray) {
            if (!firstPage) {
                doc.addPage();
                drawWatermark();
            } else {
                drawWatermark();
            }
            firstPage = false;

            const pData = protocolList.find(p => p.id === protocolId);
            const savedState = savedProtocols[protocolId as keyof typeof savedProtocols];
            const config = pData ? protocolConfigs[pData.taskId] : null;

            if (!pData || !savedState || !config) continue;

            const startPage = doc.getNumberOfPages();

            // --- 1. HEADER & CORPORATE BAR (Pass 1 logo & metadata card) ---
            // Company Logo (Aligned Left, Maintain Aspect Ratio)
            if (companyConfig.logo) {
                try {
                    const imgProps = doc.getImageProperties(companyConfig.logo);
                    const ratio = imgProps.width / imgProps.height;
                    const finalH = 15; // Fixed height
                    const finalW = finalH * ratio;
                    
                    // Aligned left with margin
                    doc.addImage(companyConfig.logo, 'PNG', 15, 6, finalW, finalH);
                } catch (e) {
                    console.error("Error adding logo to PDF", e);
                    doc.setFontSize(16);
                    doc.setFont("helvetica", "bold");
                    doc.text(companyConfig.name || "K-LYD SYSTEM", 105, 18, { align: 'center' });
                }
            } else {
                doc.setFontSize(16);
                doc.setFont("helvetica", "bold");
                doc.text(companyConfig.name || "K-LYD SYSTEM", 105, 18, { align: 'center' });
            }

            // Metadata Card Background
            doc.setDrawColor(226, 232, 240);
            doc.setFillColor(248, 250, 252);
            doc.rect(15, 26, 180, 23, 'F');
            doc.rect(15, 26, 180, 23);

            const drawMeta = (label: string, value: string, x: number, y: number) => {
                doc.setFontSize(9.5);
                doc.setFont("helvetica", "bold");
                doc.setTextColor(51, 65, 85); // Slate 700 (#334155)
                doc.text(`${label}:`, x, y);
                doc.setFont("helvetica", "normal");
                doc.setTextColor(15, 23, 42); // Slate 900 (#0f172a)
                doc.text(value, x + doc.getTextWidth(`${label}:`) + 3, y);
            };

            const drawStatusBadge = (status: string, x: number, y: number) => {
                const statusUpper = status.toUpperCase();
                let bg = [220, 252, 231]; // Light green
                let textCol = [22, 163, 74];  // Dark green
                if (statusUpper === 'RECHAZADO') {
                    bg = [254, 226, 226]; // Light red
                    textCol = [220, 38, 38];  // Dark red
                } else if (statusUpper === 'ABIERTO') {
                    bg = [219, 234, 254]; // Light blue
                    textCol = [37, 99, 235];  // Dark blue
                } else if (statusUpper === 'RESUELTO') {
                    bg = [255, 237, 213]; // Light orange
                    textCol = [234, 88, 12];  // Dark orange
                } else if (statusUpper === 'INACTIVO') {
                    bg = [241, 245, 249]; // Light gray
                    textCol = [71, 85, 105];  // Dark gray
                }
                
                doc.setFillColor(bg[0], bg[1], bg[2]);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(8.5);
                const badgeW = doc.getTextWidth(statusUpper) + 6;
                const badgeH = 5.5;
                
                doc.roundedRect(x, y - 4, badgeW, badgeH, 1, 1, 'F');
                doc.setTextColor(textCol[0], textCol[1], textCol[2]);
                doc.text(statusUpper, x + 3, y - 0.2);
            };

            const drawIdBadge = (id: string, x: number, y: number) => {
                const idStr = String(id).toUpperCase();
                doc.setFillColor(241, 245, 249); // slate-100
                doc.setFont("helvetica", "bold");
                doc.setFontSize(8.5);
                const badgeW = doc.getTextWidth(idStr) + 6;
                const badgeH = 5.5;
                doc.roundedRect(x, y - 4, badgeW, badgeH, 1, 1, 'F');
                doc.setTextColor(51, 65, 85); // slate-700
                doc.text(idStr, x + 3, y - 0.2);
            };

            let metaY = 32.5;
            drawMeta("Proyecto", projectInfo.name, 20, metaY);
            doc.setFontSize(9.5);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(51, 65, 85);
            doc.text("Protocolo ID:", 115, metaY);
            drawIdBadge(String(protocolId), 115 + doc.getTextWidth("Protocolo ID:") + 3, metaY);

            metaY += 7;
            drawMeta("Partida", pData.taskName, 20, metaY);
            doc.setFontSize(9.5);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(51, 65, 85);
            doc.text("Estado:", 115, metaY);
            drawStatusBadge(savedState.status, 115 + doc.getTextWidth("Estado:") + 3, metaY);

            metaY += 7;
            drawMeta("Ubicación", `${pData.levelName} - ${pData.unitName}`, 20, metaY);
            drawMeta("Fecha de Carga", String(savedState.creationDate), 115, metaY);

            // Separator line
            doc.setDrawColor(226, 232, 240);
            doc.line(20, metaY + 5, 190, metaY + 5);

            let currentY = metaY + 15;

            // --- 2. PARAMETERS TABLE HEADER (Framed & Symmetric) ---
            doc.setFillColor(248, 250, 252);
            doc.rect(15, currentY - 5, 180, 8, 'F');
            doc.setDrawColor(226, 232, 240);
            doc.rect(15, currentY - 5, 180, 8);

            // Symmetric Vertical Dividers (Columns: Label=120mm, R1=20mm, R2=20mm, R3=20mm)
            doc.line(135, currentY - 5, 135, currentY + 3); // label end
            doc.line(155, currentY - 5, 155, currentY + 3); // R1 end
            doc.line(175, currentY - 5, 175, currentY + 3); // R2 end

            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.setTextColor(100, 116, 139);
            doc.text("PARAMETRO DE CONTROL", 20, currentY);
            doc.text("R1", 145, currentY, { align: 'center' });
            doc.text("R2", 165, currentY, { align: 'center' });
            doc.text("R3", 185, currentY, { align: 'center' });
            currentY += 6;

            // Vectorized stylized check icon
            const drawCheckIcon = (x: number, y: number) => {
                doc.setDrawColor(34, 197, 94); // emerald-500
                doc.setLineWidth(0.6);
                doc.line(x - 2.5, y + 0.5, x - 0.5, y + 2.5);
                doc.line(x - 0.5, y + 2.5, x + 3, y - 1.5);
                doc.setLineWidth(0.1); // restore
                doc.setDrawColor(226, 232, 240); // restore frame color
            };

            // --- 3. ITEMS LOOP ---
            let itemIdx = 0;
            if (config.items) {
                for (const item of config.items) {
                    const val = (savedState.checklist as any)[item.id];

                    const labelW = 115; // Width of parameter/criterio text column (max width 120 - padding)
                    const cleanText = (txt: string) => {
                        return String(txt)
                            .replace(/⌀/g, 'Ø') // Replace unsupported diameter symbol with supported O-stroke
                            .replace(/[\u00A0\u200B-\u200D\uFEFF\t]/g, ' ')
                            .replace(/\s+/g, ' ')
                            .trim();
                    };
                    
                    const itemText = cleanText(item.text);
                    const itemCriteria = item.additionalInfo ? cleanText(item.additionalInfo) : "";

                    // Calculate Parameter height
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(9);
                    const paramLines = doc.splitTextToSize(itemText, labelW);
                    const paramH = Math.max(10, (paramLines.length * 4.5) + 5);

                    // Calculate Criterion height
                    let criteriaLines: string[] = [];
                    const hasCriteria = !!itemCriteria;
                    let criteriaH = 0;
                    if (hasCriteria) {
                        doc.setFont("helvetica", "italic");
                        doc.setFontSize(7.5);
                        criteriaLines = doc.splitTextToSize(`Criterio: ${itemCriteria}`, labelW);
                        criteriaH = Math.max(8, (criteriaLines.length * 3.8) + 4);
                    }

                    const totalItemH = paramH + criteriaH;

                    // Page Break Check for the entire block (Parameter + Criterion)
                    if (currentY + totalItemH > 275) {
                        doc.addPage();
                        drawWatermark();
                        currentY = 20; 
                    }

                    // --- DRAW PARAMETER ROW (GRAY BOX) ---
                    doc.setDrawColor(226, 232, 240); // slate-200
                    doc.setFillColor(245, 247, 250); // Premium light gray (#F5F7FA)
                    doc.rect(15, currentY, 180, paramH, 'F');
                    doc.rect(15, currentY, 180, paramH);

                    // Symmetric Vertical Dividers for checks column (only on parameter row)
                    doc.line(135, currentY, 135, currentY + paramH);
                    doc.line(155, currentY, 155, currentY + paramH);
                    doc.line(175, currentY, 175, currentY + paramH);

                    // Draw Parameter Text
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(9);
                    doc.setTextColor(30, 41, 59); // Slate 800
                    paramLines.forEach((line: string, i: number) => {
                        doc.text(line, 20, currentY + 5.5 + (i * 4.5));
                    });

                    // Render Checks on parameter row
                    const checkY = currentY + (paramH / 2);
                    const group = config.reviewerGroups?.find(g => g.unitIds.includes(pData.unitId));
                    const totalRevisions = group?.reviewers.length || 1;
                    const cols = { r1: 145, r2: 165, r3: 185 };

                    if (savedState.currentReviewerIndex >= 0 && val?.status === 'pass') drawCheckIcon(cols.r1, checkY);
                    if (savedState.currentReviewerIndex >= 1 && totalRevisions >= 2 && val?.status === 'pass') drawCheckIcon(cols.r2, checkY);
                    if (savedState.currentReviewerIndex >= 2 && totalRevisions >= 3 && val?.status === 'pass') drawCheckIcon(cols.r3, checkY);

                    currentY += paramH;

                    // --- DRAW CRITERION ROW (WHITE BOX) ---
                    if (hasCriteria) {
                        doc.setDrawColor(226, 232, 240);
                        doc.setFillColor(255, 255, 255); // White background
                        doc.rect(15, currentY, 120, criteriaH, 'F');
                        doc.rect(15, currentY, 120, criteriaH);

                        // Draw Criterion Text (smaller, italic, lighter color)
                        doc.setFont("helvetica", "italic");
                        doc.setFontSize(7.5);
                        doc.setTextColor(100, 116, 139); // Slate 500
                        criteriaLines.forEach((line: string, i: number) => {
                            doc.text(line, 20, currentY + 4.8 + (i * 3.8));
                        });

                        currentY += criteriaH;
                    }

                    // Observations
                    if (val?.comment) {
                        doc.setFont("helvetica", "bold");
                        doc.setTextColor(71, 85, 105);
                        doc.setFontSize(9);
                        doc.text("OBSERVACIÓN:", 20, currentY + 4);
                        doc.setFont("helvetica", "normal");
                        doc.setTextColor(30, 41, 59);
                        doc.text(String(val.comment), 48, currentY + 4, { maxWidth: 140 });
                        currentY += 9;
                    } else {
                        currentY += 2;
                    }

                    // --- CONDITIONAL PHOTOS (Real Images with loading) ---
                    const itemPhotos = val?.photos || [];
                    if (itemPhotos.length > 0) {
                        const boxWidth = 55;
                        const boxHeight = 40;
                        const gap = 5;

                        // Check row height page break
                        if (currentY + boxHeight > 270) {
                            doc.addPage();
                            drawWatermark();
                            currentY = 20;
                        }

                        for (let idx = 0; idx < itemPhotos.length; idx++) {
                            const photoUrl = itemPhotos[idx];
                            const x = 20 + idx * (boxWidth + gap);

                            // Picture Frame
                            doc.setDrawColor(226, 232, 240);
                            doc.rect(x, currentY, boxWidth, boxHeight);

                            // Header of the box
                            doc.setFillColor(248, 250, 252);
                            doc.rect(x, currentY, boxWidth, 6, 'F');

                            doc.setFontSize(8);
                            doc.setFont("helvetica", "bold");
                            doc.setTextColor(71, 85, 105);
                            doc.text(`Evidencia Foto ${idx + 1}`, x + boxWidth / 2, currentY + 4, { align: 'center' });

                            let imgLoaded = false;
                            if (photoUrl) {
                                try {
                                    const img = await loadImage(photoUrl);
                                    const imgX = x + 1;
                                    const imgY = currentY + 7;
                                    const imgW = boxWidth - 2;
                                    const imgH = boxHeight - 8;
                                    doc.addImage(img, 'JPEG', imgX, imgY, imgW, imgH, undefined, 'FAST');
                                    imgLoaded = true;
                                } catch (e) {
                                    console.error("Error loading item photo", e);
                                }
                            }

                            if (!imgLoaded) {
                                // Grey container with "Sin evidencia fotográfica"
                                doc.setFillColor(241, 245, 249);
                                doc.rect(x + 1, currentY + 7, boxWidth - 2, boxHeight - 8, 'F');
                                doc.setFontSize(7);
                                doc.setFont("helvetica", "normal");
                                doc.setTextColor(148, 163, 184);
                                doc.text("Sin evidencia", x + boxWidth / 2, currentY + 20, { align: 'center' });
                                doc.text("fotográfica", x + boxWidth / 2, currentY + 24, { align: 'center' });
                            }
                        }

                        currentY += boxHeight + 8;
                    }

                    if (currentY > 255) {
                        doc.addPage();
                        drawWatermark();
                        currentY = 20;
                    }
                    itemIdx++;
                }
            }

            // --- 4. AUDIT HISTORY (Rejections & Resolutions with Evidencia / Adjuntos) ---
            const auditHistory = savedState.history.filter(h => h.action === 'rejected' || h.action === 'resolved');
            if (auditHistory.length > 0) {
                if (currentY > 240) {
                    doc.addPage();
                    drawWatermark();
                    currentY = 20;
                } else {
                    currentY += 2;
                }

                // Pre-load all history images
                const historyImagesMap: Record<string, HTMLImageElement[]> = {};
                for (const entry of auditHistory) {
                    const entryId = `${entry.date}_${entry.user}`;
                    historyImagesMap[entryId] = [];
                    if (entry.images && entry.images.length > 0) {
                        for (const url of entry.images) {
                            try {
                                const img = await loadImage(url);
                                historyImagesMap[entryId].push(img);
                            } catch (e) {
                                console.error("Error loading history image", e);
                            }
                        }
                    }
                }

                const tableBody = auditHistory.map(h => {
                    const comment = h.comment || (h.action === 'resolved' ? 'Sin detalle' : 'Sin motivo');
                    const detailText = h.action === 'resolved' 
                        ? `[OK] RESOLUCIÓN: ${comment.replace(/^Resolución:\s*/i, '')}`
                        : comment;
                    const entryId = `${h.date}_${h.user}`;
                    const loadedImgs = historyImagesMap[entryId] || [];
                    
                    return {
                        user: h.user,
                        date: h.date,
                        detail: detailText,
                        evidence: loadedImgs.length > 0 ? '' : 'S/E',
                        loadedImgs: loadedImgs
                    };
                });

                autoTable(doc, {
                    startY: currentY,
                    margin: { left: 20, right: 20 },
                    head: [
                        [{ content: 'HISTORIAL (RECHAZOS Y RESOLUCIONES)', colSpan: 4, styles: { halign: 'center', fillColor: [185, 28, 28], fontStyle: 'bold' } }],
                        [
                            { content: 'Responsable', styles: { fontStyle: 'bold', fillColor: [254, 242, 242] } },
                            { content: 'Fecha y Hora', styles: { fontStyle: 'bold', fillColor: [254, 242, 242] } },
                            { content: 'Detalle / Motivo', styles: { fontStyle: 'bold', fillColor: [254, 242, 242] } },
                            { content: 'Evidencia / Adjuntos', styles: { fontStyle: 'bold', fillColor: [254, 242, 242] } }
                        ]
                    ],
                    body: tableBody as any[],
                    columns: [
                        { header: 'Responsable', dataKey: 'user' },
                        { header: 'Fecha y Hora', dataKey: 'date' },
                        { header: 'Detalle / Motivo', dataKey: 'detail' },
                        { header: 'Evidencia / Adjuntos', dataKey: 'evidence' }
                    ],
                    theme: 'grid',
                    styles: { fontSize: 7, cellPadding: 1.2, lineWidth: 0.1 },
                    columnStyles: {
                        user: { cellWidth: 35 },
                        date: { cellWidth: 40 },
                        detail: { cellWidth: 'auto' },
                        evidence: { cellWidth: 45, halign: 'center', valign: 'middle' }
                    },
                    didParseCell: (data) => {
                        if (data.column.dataKey === 'evidence') {
                            const rowData = data.row.raw as any;
                            if (rowData && rowData.loadedImgs && rowData.loadedImgs.length > 0) {
                                data.cell.styles.minCellHeight = 18;
                            }
                        }
                    },
                    didDrawCell: (data) => {
                        if (data.column.dataKey === 'evidence' && data.cell.section === 'body') {
                            const rowData = data.row.raw as any;
                            if (rowData && rowData.loadedImgs && rowData.loadedImgs.length > 0) {
                                const cell = data.cell;
                                const x = cell.x + 2;
                                const y = cell.y + 2;
                                const thumbW = 12;
                                const thumbH = 12;
                                const gap = 2;
                                
                                rowData.loadedImgs.forEach((img: HTMLImageElement, i: number) => {
                                    if (i < 3) {
                                        const imgX = x + i * (thumbW + gap);
                                        if (imgX + thumbW <= cell.x + cell.width) {
                                            try {
                                                doc.addImage(img, 'JPEG', imgX, y, thumbW, thumbH, undefined, 'FAST');
                                            } catch (e) {
                                                console.error("Error drawing thumbnail in cell", e);
                                            }
                                        }
                                    }
                                });
                            }
                        }
                    }
                });
                currentY = (doc as any).lastAutoTable.finalY + 8;
            }

            // --- 5. CORPORATE FOOTER (Signatures Grid) ---
            const groupSignatures = config.reviewerGroups?.find(g => g.unitIds.includes(pData.unitId));
            const reviewers = groupSignatures?.reviewers || [];
            const N = reviewers.length || 1;
            
            const maxCols = 3;
            const M = Math.ceil(N / maxCols); // number of rows
            
            const boxW = 54;
            const boxH = 40;
            const colGap = 9;
            const rowGap = 6;
            
            const totalSigHeight = 7 + 10 + M * boxH + (M - 1) * rowGap;
            const targetY = 283 - totalSigHeight;

            if (currentY > targetY) {
                doc.addPage();
                drawWatermark();
                currentY = 20;
            } else {
                currentY = targetY; // Align to bottom of the page
            }

            // Signatures Section Header (Framed)
            doc.setFillColor(248, 250, 252);
            doc.rect(15, currentY, 180, 7, 'F');
            doc.setDrawColor(226, 232, 240);
            doc.rect(15, currentY, 180, 7);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(8.5);
            doc.setTextColor(100, 116, 139);
            doc.text("FIRMAS Y APROBACIONES", 105, currentY + 4.5, { align: 'center' });

            const sigY = currentY + 10;

            for (let idx = 0; idx < N; idx++) {
                const col = idx % maxCols;
                const row = Math.floor(idx / maxCols);
                
                const x = 15 + col * (boxW + colGap);
                const y = sigY + row * (boxH + rowGap);
                
                doc.setDrawColor(226, 232, 240); // slate-200
                doc.setFillColor(255, 255, 255);
                doc.roundedRect(x, y, boxW, boxH, 1.5, 1.5, 'FD'); // border-radius rounded corners

                const signatureData = savedState.signatures[idx];
                const reviewer = reviewers[idx];
                const reviewerName = reviewer ? reviewer.name : `Revisor ${idx + 1}`;
                const reviewerRole = reviewer ? getReviewerRole(reviewer.id) : "Revisor";

                // Clean reviewer name from admin suffix
                const cleanReviewerName = reviewerName
                    .replace(/\(como admin\)|\(Admin del Proyecto\)/gi, '')
                    .trim();

                let sigImageDrawn = false;
                if (signatureData) {
                    try {
                        // Center signature inside the 54mm x 18mm area (top section)
                        const imgW = 34;
                        const imgH = 12;
                        const imgX = x + (boxW - imgW) / 2;
                        const imgY = y + 1 + (18 - imgH) / 2;
                        
                        doc.addImage(signatureData, 'PNG', imgX, imgY, imgW, imgH, undefined, 'FAST');
                        sigImageDrawn = true;
                    } catch (e) {
                        console.error("Error drawing signature image", e);
                    }
                }

                if (!sigImageDrawn) {
                    doc.setFontSize(8.5);
                    doc.setFont("helvetica", "italic");
                    doc.setTextColor(156, 163, 175); // Slate 400
                    doc.text("Pendiente de firma", x + boxW / 2, y + 11, { align: 'center' });
                }

                // Dashed line below graphic area
                doc.setDrawColor(203, 213, 225); // gray-300
                doc.setLineWidth(0.3);
                doc.setLineDashPattern([1.5, 1.5], 0);
                doc.line(x + 4, y + 20.5, x + boxW - 4, y + 20.5);
                doc.setLineDashPattern([], 0); // Restore solid
                doc.setLineWidth(0.1);

                // Name of Revisor (nombre del usuario)
                doc.setFontSize(8);
                doc.setFont("helvetica", "bold");
                doc.setTextColor(30, 41, 59); // Slate 800
                doc.text(cleanReviewerName, x + boxW / 2, y + 25, { align: 'center', maxWidth: boxW - 6 });

                // Cargo / Rol / PERFIL (uppercase, blue-600)
                doc.setFontSize(7);
                doc.setFont("helvetica", "bold");
                doc.setTextColor(37, 99, 235); // Blue 600
                doc.text(reviewerRole.toUpperCase(), x + boxW / 2, y + 29, { align: 'center', maxWidth: boxW - 6 });

                const sigDateStr = signatureData 
                    ? getSignatureDate(idx, reviewerName, savedState.history) 
                    : "-";
                doc.setFontSize(6.5);
                doc.setFont("helvetica", "normal");
                doc.setTextColor(156, 163, 175); // Slate 400
                doc.text(sigDateStr, x + boxW / 2, y + 33, { align: 'center' });
            }

            currentY = sigY + M * boxH + (M - 1) * rowGap;

            // --- 6. DECORATE ALL PAGES FOR THIS PROTOCOL (Pass 2) ---
            const endPage = doc.getNumberOfPages();
            for (let p = startPage; p <= endPage; p++) {
                doc.setPage(p);
                
                // Corporate top bar
                doc.setFillColor(26, 115, 232);
                doc.rect(0, 0, 210, 4, 'F');
                
                // Footer
                doc.setFontSize(7);
                doc.setTextColor(148, 163, 184);
                doc.text(`Generado por K-LYD System - ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, 105, 287, { align: 'center' });
                doc.text(`Página ${p}`, 190, 287, { align: 'right' });
            }
        }

        doc.save(`Protocolo_${selectedExportTaskId}_${Date.now()}.pdf`);
        setExportModalOpen(false);
        setSelectedProtocolIds(new Set());
    };

    return (
        <div className="flex flex-col h-full bg-background-light dark:bg-background-dark">
            {/* ... (Existing JSX for Header and Table - No changes required here) ... */}
            {/* Top Blue Toolbar */}
            <div className="bg-[#1a73e8] dark:bg-blue-900 text-white h-14 px-4 md:px-6 flex items-center justify-between shrink-0 shadow-md z-30">
                <div className="flex items-center gap-6">
                    <h1 className="text-lg font-bold">Control de Calidad</h1>
                    <div className="relative hidden md:block">
                        <button
                            onClick={() => setMenuOpen(!menuOpen)}
                            className={`flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded transition-colors ${menuOpen ? 'bg-white/20' : 'hover:bg-white/10'}`}
                        >
                            <span className="material-symbols-outlined text-[18px]">assignment</span>
                            Protocolos
                            <span className="material-symbols-outlined text-[16px]">expand_more</span>
                        </button>
                        {menuOpen && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)}></div>
                                <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-50 animate-in fade-in zoom-in duration-100">
                                    <button
                                        onClick={() => { setMenuOpen(false); setConfigModalOpen(true); }}
                                        className={`w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 ${!hasPermission("cc_config", "edit") ? "opacity-50 pointer-events-none" : ""}`}
                                    >
                                        <span className="material-symbols-outlined text-[18px] text-slate-400">settings</span>
                                        Configuración Protocolos
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                
                {/* Right side: Project Name (Desktop) OR Widgets (Mobile) */}
                <div className="flex items-center gap-2">
                    <div className="text-xs text-blue-100 hidden sm:block truncate mr-2">Proyecto Activo: {projectInfo.name}</div>
                    
                    {/* Mobile triggers in the blue header bar */}
                    <div className="flex md:hidden items-center gap-1.5 shrink-0">
                        <button 
                            onClick={() => window.dispatchEvent(new CustomEvent('open-bug-report'))}
                            className="w-10 h-10 bg-rose-600 border border-white/20 text-white rounded-xl shadow-md flex items-center justify-center transition-all hover:scale-105 active:scale-95"
                            title="Reportar Falla"
                        >
                            <span className="material-symbols-outlined text-[20px]">bug_report</span>
                        </button>
                        <button 
                            onClick={() => window.dispatchEvent(new CustomEvent('open-ai-assistant'))}
                            className="w-10 h-10 bg-white/20 border border-white/20 text-white rounded-xl shadow-md flex items-center justify-center transition-all hover:scale-105 active:scale-95 backdrop-blur-sm"
                            title="Asistente IA"
                        >
                            <svg viewBox="0 0 100 100" className="w-[20px] h-[20px]" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M25 50 C25 25, 75 25, 75 50" stroke="currentColor" strokeWidth="6" strokeLinecap="round" fill="none" />
                                <rect x="18" y="40" width="8" height="20" rx="4" fill="currentColor" />
                                <rect x="74" y="40" width="8" height="20" rx="4" fill="currentColor" />
                                <circle cx="40" cy="50" r="4.5" fill="currentColor" />
                                <circle cx="60" cy="50" r="4.5" fill="currentColor" />
                                <path d="M42 63 C45 67, 55 67, 58 63" stroke="currentColor" strokeWidth="4" strokeLinecap="round" fill="none" />
                                <path d="M78 56 C78 72, 68 82, 52 82" stroke="currentColor" strokeWidth="4.5" strokeLinecap="round" fill="none" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col overflow-y-auto bg-background-light dark:bg-background-dark p-6">
                <div className="mx-auto w-full max-w-[1400px] flex flex-col gap-6 min-h-full">
                    <div className="hidden md:block">
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">LISTADO MAESTRO DE PROTOCOLOS</h1>
                        <p className="text-slate-500 dark:text-slate-400">Listado maestro de protocolos vinculados a Presupuesto.</p>
                    </div>

                    <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col md:flex-row gap-4 justify-between items-center">
                        <div className="flex items-center gap-4 w-full md:w-auto">
                            <div className="relative w-full md:w-96">
                                <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-[20px]">search</span>
                                <input
                                    type="text"
                                    placeholder="Buscar por número, partida o recinto..."
                                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 text-sm outline-none focus:ring-2 focus:ring-primary focus:bg-white dark:focus:bg-slate-900 transition-all"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                            <button
                                onClick={() => setExportModalOpen(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-sm shrink-0"
                            >
                                <span className="material-symbols-outlined text-[20px] text-blue-600">download</span>
                                Exportar
                            </button>
                        </div>
                        <div className="w-full md:w-auto">
                            <select
                                value={selectedPartida}
                                onChange={(e) => setSelectedPartida(e.target.value)}
                                className="w-full md:w-48 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm font-medium text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-primary cursor-pointer transition-all hover:border-slate-300"
                            >
                                <option>Todas las Partidas</option>
                                {uniquePartidas.map(name => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex-1 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col">
                        <div className="overflow-auto flex-1">
                            <table className="w-full text-left border-collapse hidden md:table">
                                <thead className="bg-white dark:bg-slate-800 sticky top-0 z-10">
                                    <tr className="border-b border-slate-100 dark:border-slate-700 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                                        <th className="px-6 py-4">Número</th>
                                        <th className="px-6 py-4">Partida</th>
                                        <th className="px-6 py-4">Estado</th>
                                        <th className="px-6 py-4">Nivel</th>
                                        <th className="px-6 py-4">Unidad Física</th>
                                        <th className="px-6 py-4">Etapa</th>
                                        <th className="px-6 py-4 text-right">Fecha Mod.</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50 dark:divide-slate-700">
                                    {paginatedProtocols.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="px-6 py-12 text-center text-slate-400">
                                                <div className="flex flex-col items-center gap-3">
                                                    <span className="material-symbols-outlined text-4xl opacity-50">toc</span>
                                                    <p>No hay protocolos generados.</p>
                                                    <p className="text-xs max-w-md">Configure los protocolos en el menú "Configuración" y asegúrese de asignar unidades en la EDT (columna Calidad) para generar items aquí.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedProtocols.map((item: ProtocolInstance) => {
                                            // Check if reviewed by the current user
                                            const isRevisadoPorMi = checkIsRevisadoPorMi(item);

                                            return (
                                                <tr
                                                    key={item.id}
                                                    onClick={() => handleOpenProtocolDetail(item)}
                                                    className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-all group cursor-pointer border-l-4 ${
                                                        item.status === 'Rechazado'
                                                            ? 'bg-red-50/50 dark:bg-red-950/10 border-l-red-500 hover:bg-red-50 dark:hover:bg-red-950/25'
                                                            : item.status === 'Resuelto'
                                                                ? 'bg-orange-50/50 dark:bg-orange-950/10 border-l-orange-500 hover:bg-orange-50 dark:hover:bg-orange-950/25'
                                                                : isRevisadoPorMi
                                                                    ? 'bg-green-50/30 dark:bg-green-950/10 border-l-green-450 hover:bg-green-50/50 dark:hover:bg-green-950/20'
                                                                    : item.status === 'Cerrado'
                                                                        ? 'border-l-emerald-600'
                                                                        : 'border-l-transparent'
                                                    }`}
                                                >
                                                    <td className="px-6 py-4">
                                                        <span className={`font-bold text-sm ${item.status === 'Inactivo' ? 'text-slate-400 dark:text-slate-500' : 'text-slate-800 dark:text-slate-200'}`}>{item.id}</span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex flex-col">
                                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                                <span className={`text-sm font-bold ${item.status === 'Inactivo' ? 'text-slate-400 dark:text-slate-500' : 'text-slate-900 dark:text-white'}`}>{item.protocolName}</span>
                                                                {item.isObsolete && (
                                                                    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[9px] font-black bg-amber-500 text-white animate-pulse">
                                                                        ⚠️ Fuera de Gantt
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <span className="text-[11px] text-slate-400 font-medium">{item.taskIndex} {item.taskName}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-bold ${
                                                            item.status === 'Abierto'
                                                                ? (isRevisadoPorMi
                                                                    ? 'bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800/40'
                                                                    : 'bg-blue-50 text-blue-600 border border-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-900/30')
                                                                : item.status === 'Inactivo'
                                                                    ? 'bg-slate-100 text-slate-400 border border-slate-200 dark:bg-slate-800 dark:text-slate-600'
                                                                    : item.status === 'Rechazado'
                                                                        ? 'bg-red-600 text-white border border-red-700 dark:bg-red-700 dark:border-red-800'
                                                                        : item.status === 'Resuelto'
                                                                            ? 'bg-orange-500 text-white border border-orange-600 dark:bg-orange-600 dark:border-orange-700'
                                                                            : item.status === 'Cerrado'
                                                                                ? 'bg-emerald-800 text-white border border-emerald-900 dark:bg-emerald-900 dark:border-emerald-950'
                                                                                : 'bg-green-50 text-green-600 border border-green-200 dark:bg-green-900/20 dark:text-green-400'
                                                        }`}>
                                                            {isRevisadoPorMi ? 'Revisado por Mí' : item.status}
                                                        </span>
                                                    </td>
                                                <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                                                    {item.levelName}
                                                </td>
                                                <td className="px-6 py-4 text-sm font-medium text-slate-800 dark:text-slate-200">
                                                    {item.unitName}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">
                                                    {item.stage}
                                                </td>
                                                <td className="px-6 py-4 text-right text-xs font-mono text-slate-500 dark:text-slate-500">
                                                    {item.date}
                                                </td>
                                            </tr>
                                        ); })
                                    )}
                                </tbody>
                            </table>

                            {/* VISTA MÓVIL: Drill-Down */}
                            <div className="md:hidden flex flex-col p-4 gap-4 bg-white dark:bg-slate-900/50 border-t border-slate-200">
                                {!mobileSelectedUnit ? (
                                    // VISTA 1: Grilla de Sitios agrupados por Nivel
                                    (() => {
                                        if (filteredProtocolList.length === 0) {
                                            return <div className="text-center text-slate-400 py-8 italic flex flex-col items-center gap-2"><span className="material-symbols-outlined text-4xl opacity-50">toc</span>No hay protocolos generados.</div>;
                                        }

                                        // Agrupar protocolos por Manzana -> Sitio
                                        const groups: Record<string, ProtocolInstance[]> = {};
                                        filteredProtocolList.forEach(p => {
                                            const key = `${p.levelName}__${p.unitName}__${p.unitId}`;
                                            if (!groups[key]) groups[key] = [];
                                            groups[key].push(p);
                                        });

                                        const levelsMap: Record<string, { unitId: string, unitName: string, levelName: string, protocols: ProtocolInstance[] }[]> = {};
                                        Object.keys(groups).forEach(k => {
                                            const parts = k.split('__');
                                            const levelName = parts[0];
                                            const unitName = parts[1];
                                            const unitId = parts[2];
                                            if (!levelsMap[levelName]) levelsMap[levelName] = [];
                                            levelsMap[levelName].push({ unitId, unitName, levelName, protocols: groups[k] });
                                        });

                                        return (
                                            <div className="flex flex-col gap-6">
                                                {Object.keys(levelsMap).sort().map(levelName => (
                                                    <div key={levelName} className="flex flex-col gap-4 items-center">
                                                        <h3 className="font-bold text-slate-800 dark:text-white pb-1">{levelName}</h3>
                                                        <div className="flex flex-wrap justify-center gap-3">
                                                            {levelsMap[levelName].sort((a,b) => a.unitName.localeCompare(b.unitName)).map(unitData => {
                                                                // Validar estados para colorear el botón de la unidad
                                                                const hasRejected = unitData.protocols.some(p => p.status === 'Rechazado');
                                                                const hasResolved = unitData.protocols.some(p => p.status === 'Resuelto');
                                                                const allClosed = unitData.protocols.length > 0 && unitData.protocols.every(p => p.status === 'Cerrado');
                                                                const allClosedOrReviewed = unitData.protocols.length > 0 && unitData.protocols.every(p => p.status === 'Cerrado' || checkIsRevisadoPorMi(p));
                                                                
                                                                const buttonBgClass = hasRejected 
                                                                    ? 'bg-red-600' 
                                                                    : hasResolved 
                                                                        ? 'bg-orange-500' 
                                                                        : allClosedOrReviewed
                                                                            ? (allClosed ? 'bg-emerald-800' : 'bg-green-500')
                                                                            : 'bg-[#29b6f6]';


                                                                return (
                                                                    <button
                                                                        key={unitData.unitId}
                                                                        onClick={() => setMobileSelectedUnit({ unitId: unitData.unitId, unitName: unitData.unitName, levelName: unitData.levelName })}
                                                                        className={`w-20 h-10 px-1 rounded-xl text-xs font-bold text-white shadow-md flex items-center justify-center transition-transform active:scale-95 border border-black/10 ${buttonBgClass}`}
                                                                    >
                                                                        <span className="truncate w-full text-center">{unitData.unitName}</span>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()
                                ) : (
                                    // VISTA 2: Listado de Protocolos del Sitio (wireframe derecha)
                                    <div className="flex flex-col gap-4">
                                        <div className="flex items-center gap-3 pb-2 pt-2 border-slate-200 dark:border-slate-700">
                                            <button
                                                onClick={() => setMobileSelectedUnit(null)}
                                                className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                            >
                                                <span className="material-symbols-outlined text-slate-700 dark:text-slate-300">arrow_back</span>
                                            </button>
                                            <div className="flex flex-col">
                                                <h3 className="font-bold text-slate-800 dark:text-white text-lg leading-tight">Protocolos</h3>
                                                <span className="text-xs font-bold text-slate-600 dark:text-slate-400">{mobileSelectedUnit.levelName}, {mobileSelectedUnit.unitName}</span>
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-3">
                                            {filteredProtocolList.filter(p => p.unitId === mobileSelectedUnit.unitId).map(p => {
                                                const isRevisadoPorMi = checkIsRevisadoPorMi(p);
                                                const cardBgClass = p.status === 'Rechazado'
                                                    ? 'bg-red-600 text-white font-bold animate-pulse'
                                                    : p.status === 'Resuelto'
                                                        ? 'bg-orange-500 text-white font-bold'
                                                        : isRevisadoPorMi
                                                            ? 'bg-green-500 text-white font-bold'
                                                            : p.status === 'Cerrado'
                                                                ? 'bg-emerald-800 text-white'
                                                                : 'bg-[#7fc2ea] text-white';

                                                return (
                                                    <div
                                                        key={p.id}
                                                        onClick={() => handleOpenProtocolDetail(p)}
                                                        className={`rounded-lg p-3 cursor-pointer active:scale-[0.98] transition-transform shadow-sm border border-black/10
                                                            ${cardBgClass} ${!hasPermission("cc_completar", "view") ? "opacity-50 pointer-events-none" : ""}`}
                                                    >
                                                        <div className="flex items-center justify-between gap-1.5">
                                                            <div className="font-bold text-[13px] tracking-tight leading-snug drop-shadow-sm">{p.protocolName}</div>
                                                            {p.isObsolete && (
                                                                <span className="bg-amber-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full shrink-0">
                                                                    ⚠️ Fuera Gantt
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="px-6 py-3 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-xs text-slate-500 flex justify-between items-center whitespace-nowrap overflow-x-auto no-scrollbar">
                            <div className="flex items-center gap-4">
                                <span>
                                    Mostrando {Math.min(filteredProtocolList.length, (currentPage - 1) * ITEMS_PER_PAGE + 1)}-{Math.min(filteredProtocolList.length, currentPage * ITEMS_PER_PAGE)} de {filteredProtocolList.length} protocolos
                                </span>
                            </div>
                            
                            {totalPages > 1 && (
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="p-1 px-2 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-[16px] leading-none">chevron_left</span>
                                    </button>
                                    
                                    <div className="flex items-center gap-1 px-2">
                                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                                            .filter(page => {
                                                // Show first, last, current and 1 neighbor
                                                return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                                            })
                                            .map((page, i, arr) => {
                                                const showEllipsis = i > 0 && page - arr[i-1] > 1;
                                                return (
                                                    <React.Fragment key={page}>
                                                        {showEllipsis && <span className="px-1 text-slate-400">...</span>}
                                                        <button
                                                            onClick={() => setCurrentPage(page)}
                                                            className={`min-w-[28px] h-7 rounded text-[11px] font-bold transition-all ${
                                                                currentPage === page
                                                                    ? 'bg-primary text-white shadow-sm'
                                                                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                                                            }`}
                                                        >
                                                            {page}
                                                        </button>
                                                    </React.Fragment>
                                                );
                                            })}
                                    </div>

                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className="p-1 px-2 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-[16px] leading-none">chevron_right</span>
                                    </button>
                                </div>
                            )}
                            
                            <span className="hidden sm:inline">Página {filteredProtocolList.length > 0 ? currentPage : 0} de {totalPages}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- MODALS --- */}
            {protocolDetailModalOpen && activeProtocolId && (() => {
                const protocolData = getActiveProtocolData();
                const config = getActiveConfig();
                const currentState = getCurrentState();
                const items = config?.items || [];
                const currentReviewerName = getReviewerName(currentState.currentReviewerIndex, config, protocolData?.unitId);
                const group = config?.reviewerGroups?.find(g => g.unitIds.includes(protocolData?.unitId || ''));
                const isLastReviewer = group ? currentState.currentReviewerIndex >= group.reviewers.length - 1 : true;
                const canReject = currentState.currentReviewerIndex > 0;
                const isEditable = isUserTurnExactly(activeProtocolId) && currentState.status !== 'Cerrado';

                // Get rejection reason if current status is Rejected
                const isRejected = currentState.status === 'Rechazado';
                const lastRejectionReason = isRejected ? getLastRejectionReason(currentState.history) : "";

                const incompleteItems = items.some(item => {
                    const itemState = tempChecklist[item.id];
                    return !itemState || itemState.status === 'pending';
                });
                const hasFail = items.some(item => tempChecklist[item.id]?.status === 'fail');
                const canSend = isEditable && !(currentState.currentReviewerIndex > 0 && hasFail);

                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-4xl border border-slate-200 dark:border-slate-800 flex flex-col h-[90vh] overflow-hidden">
                            {/* Modal Header */}
                            <div className="flex flex-col md:flex-row md:justify-between items-start p-4 md:p-6 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 z-10 gap-4">
                                <div className="flex-1 pr-2 w-full">
                                    <div className="flex justify-between items-start md:hidden mb-2">
                                        <div className="text-left shrink-0">
                                            {isProjectAdmin() && group && group.reviewers.length > 1 ? (
                                                <div className="flex flex-col items-start">
                                                    <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Revisión (Admin)</label>
                                                    <select
                                                        value={currentState.currentReviewerIndex}
                                                        onChange={(e) => {
                                                            const newIdx = parseInt(e.target.value);
                                                            setSavedProtocols(prev => {
                                                                const current = prev[activeProtocolId] || {
                                                                    id: activeProtocolId,
                                                                    taskId: protocolData?.taskId || '',
                                                                    unitId: protocolData?.unitId || '',
                                                                    groupId: group?.id || '',
                                                                    status: 'Abierto',
                                                                    currentReviewerIndex: 0,
                                                                    checklist: {},
                                                                    signatures: {},
                                                                    history: [],
                                                                    creationDate: new Date().toLocaleDateString('es-CL')
                                                                };
                                                                return {
                                                                    ...prev,
                                                                    [activeProtocolId]: { ...current, currentReviewerIndex: newIdx }
                                                                };
                                                            });
                                                            const savedState = savedProtocols[activeProtocolId];
                                                            setCurrentSignature(savedState?.signatures[newIdx] || null);
                                                        }}
                                                        className="text-xs p-1 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 rounded font-bold text-primary outline-none focus:ring-1 focus:ring-primary/50"
                                                    >
                                                        {group.reviewers.map((rev, i) => (
                                                            <option key={i} value={i}>Revisor {i + 1} ({rev.name})</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ) : (
                                                <>
                                                    <p className="text-[10px] font-bold text-primary uppercase tracking-wider">{getReviewerName(currentState.currentReviewerIndex, config, protocolData?.unitId)}</p>
                                                    <div className="h-0.5 w-full bg-primary mt-1"></div>
                                                </>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            {location.state?.fromPlanning && (
                                                <button onClick={() => navigate(-1)} className="p-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-lg transition-colors flex items-center justify-center">
                                                    <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                                                </button>
                                            )}
                                            <button onClick={handleCloseProtocolModal} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1">
                                                <span className="material-symbols-outlined text-[24px]">close</span>
                                            </button>
                                        </div>
                                    </div>
                                    <h2 className="text-sm md:text-xl font-black text-slate-900 dark:text-white flex flex-col md:flex-row items-start md:items-center gap-0.5 md:gap-3 leading-tight uppercase">
                                        {protocolData?.protocolName}
                                        <span className="text-[11px] md:text-sm font-bold text-slate-500 md:border-l md:border-slate-300 md:pl-3 md:ml-1 tracking-wider">{protocolData?.id}</span>
                                    </h2>
                                    <p className="text-xs md:text-sm text-slate-500 mt-1.5 leading-snug">{protocolData?.taskName} • {protocolData?.unitName} ({protocolData?.levelName})</p>
                                </div>
                                <div className="hidden md:flex items-center gap-2 shrink-0">
                                    <div className="text-right mr-4 shrink-0">
                                        {isProjectAdmin() && group && group.reviewers.length > 1 ? (
                                            <div className="flex flex-col items-end">
                                                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Revisión (Admin)</label>
                                                <select
                                                    value={currentState.currentReviewerIndex}
                                                    onChange={(e) => {
                                                        const newIdx = parseInt(e.target.value);
                                                        setSavedProtocols(prev => {
                                                            const current = prev[activeProtocolId] || {
                                                                id: activeProtocolId,
                                                                taskId: protocolData?.taskId || '',
                                                                unitId: protocolData?.unitId || '',
                                                                groupId: group?.id || '',
                                                                status: 'Abierto',
                                                                currentReviewerIndex: 0,
                                                                checklist: {},
                                                                signatures: {},
                                                                history: [],
                                                                creationDate: new Date().toLocaleDateString('es-CL')
                                                            };
                                                            return {
                                                                ...prev,
                                                                [activeProtocolId]: { ...current, currentReviewerIndex: newIdx }
                                                                };
                                                        });
                                                        const savedState = savedProtocols[activeProtocolId];
                                                        setCurrentSignature(savedState?.signatures[newIdx] || null);
                                                    }}
                                                    className="text-xs p-1 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 rounded font-bold text-primary outline-none focus:ring-1 focus:ring-primary/50"
                                                >
                                                    {group.reviewers.map((rev, i) => (
                                                        <option key={i} value={i}>Revisor {i + 1} ({rev.name})</option>
                                                    ))}
                                                </select>
                                            </div>
                                        ) : (
                                            <>
                                                <p className="text-xs font-bold text-primary uppercase tracking-wider">{getReviewerName(currentState.currentReviewerIndex, config, protocolData?.unitId)}</p>
                                                <div className="h-0.5 w-full bg-primary mt-1"></div>
                                            </>
                                        )}
                                    </div>
                                    {location.state?.fromPlanning && (
                                        <button onClick={() => navigate(-1)} className="mr-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-lg text-xs font-bold transition-colors flex items-center gap-1">
                                            <span className="material-symbols-outlined text-[16px]">arrow_back</span>
                                            <span>Volver a Planificación</span>
                                        </button>
                                    )}
                                    <button onClick={handleCloseProtocolModal} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                                        <span className="material-symbols-outlined text-[28px]">close</span>
                                    </button>
                                </div>
                            </div>

                            {/* Modal Body (Scrollable) */}
                            <div className="flex-1 overflow-y-auto p-8 bg-white dark:bg-slate-900">
                                {/* Checklist */}
                                <div className="space-y-2 md:space-y-6">
                                    {items.length === 0 ? (
                                        <p className="text-center text-slate-400 py-10 italic">No hay parámetros de revisión configurados para este protocolo.</p>
                                    ) : (
                                        items.map((item, idx) => {
                                            const itemState = tempChecklist[item.id] || { status: 'pending', comment: '', photos: [] };
                                            return (
                                                <div key={item.id} className="border-b border-slate-100 dark:border-slate-800 last:border-0 py-2 md:py-4">
                                                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-1.5 md:gap-3">
                                                        <div className="flex gap-2 md:gap-4 w-full md:max-w-[50%]">
                                                            <span className="font-bold text-slate-900 dark:text-slate-200 text-sm mt-0.5 md:mt-1.5">{idx + 1}.</span>
                                                            <span className="text-slate-700 dark:text-slate-300 text-sm mt-1.5 leading-relaxed">{item.text}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-1 shadow-sm shrink-0">
                                                            {/* Info */}
                                                            <button
                                                                onClick={() => handleOpenInfoModal(item.id, true)}
                                                                className={`p-1.5 rounded transition-all hover:bg-slate-100 dark:hover:bg-slate-700 ${item.additionalInfo ? 'text-slate-500' : 'text-slate-300'}`}
                                                                title="Información"
                                                            >
                                                                <span className="material-symbols-outlined text-[20px] font-bold">info</span>
                                                            </button>

                                                            <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1"></div>

                                                            {/* Pass */}
                                                            <button
                                                                onClick={() => isEditable && handleChecklistClick(item.id, 'pass')}
                                                                disabled={!isEditable}
                                                                className={`p-1.5 rounded-md transition-all ${itemState.status === 'pass' ? 'bg-green-500 text-white shadow-sm' : 'text-slate-300'} ${isEditable ? 'hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-green-500 cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                                                                title="Cumple"
                                                            >
                                                                <span className="material-symbols-outlined text-[20px] font-bold">check_circle</span>
                                                            </button>

                                                            {/* Fail */}
                                                            <button
                                                                onClick={() => isEditable && handleChecklistClick(item.id, 'fail')}
                                                                disabled={!isEditable}
                                                                className={`p-1.5 rounded-md transition-all ${itemState.status === 'fail' ? 'bg-red-500 text-white shadow-sm' : 'text-slate-300'} ${isEditable ? 'hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-red-500 cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                                                                title="No Cumple"
                                                            >
                                                                <span className="material-symbols-outlined text-[20px] font-bold">cancel</span>
                                                            </button>

                                                            {/* N/A */}
                                                            <button
                                                                onClick={() => isEditable && handleChecklistClick(item.id, 'na')}
                                                                disabled={!isEditable}
                                                                className={`p-1.5 rounded-md transition-all ${itemState.status === 'na' ? 'bg-slate-500 text-white shadow-sm' : 'text-slate-300'} ${isEditable ? 'hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-500 cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                                                                title="No Aplica"
                                                            >
                                                                <span className="material-symbols-outlined text-[20px] font-bold">remove_circle</span>
                                                            </button>

                                                            <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1"></div>

                                                            {/* Comment */}
                                                            <button
                                                                onClick={() => handleCommentClick(item.id)}
                                                                className={`p-1.5 rounded transition-all hover:bg-slate-100 dark:hover:bg-slate-700 ${itemState.comment ? 'text-amber-500' : 'text-slate-300 hover:text-amber-500'}`}
                                                                title="Comentarios"
                                                            >
                                                                <span className="material-symbols-outlined text-[20px] font-bold">chat_bubble</span>
                                                            </button>

                                                            {/* Camera (NEW) */}
                                                            <button
                                                                onClick={() => handlePhotoClick(item.id)}
                                                                className={`p-1.5 rounded transition-all hover:bg-slate-100 dark:hover:bg-slate-700 ${itemState.photos && itemState.photos.length > 0 ? 'text-blue-500' : 'text-slate-300 hover:text-blue-500'}`}
                                                                title="Adjuntar Fotos"
                                                            >
                                                                <span className="material-symbols-outlined text-[20px] font-bold">photo_camera</span>
                                                                {itemState.photos && itemState.photos.length > 0 && (
                                                                    <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[9px] w-4 h-4 flex items-center justify-center rounded-full border border-white dark:border-slate-800">
                                                                        {itemState.photos.length}
                                                                    </span>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Inherited Data Block */}
                                                    {(() => {
                                                        const initialItemState = initialChecklist[item.id];
                                                        const hasInheritedComment = initialItemState && initialItemState.comment;
                                                        const hasInheritedPhotos = initialItemState && initialItemState.photos && initialItemState.photos.length > 0;
                                                        
                                                        if (currentState.currentReviewerIndex > 0 && (hasInheritedComment || hasInheritedPhotos)) {
                                                            return (
                                                                <div className="mt-3 ml-6 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/40 border border-slate-200/60 dark:border-slate-700/50 text-xs text-slate-600 dark:text-slate-400 animate-in fade-in duration-200">
                                                                    <div className="font-bold text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1">
                                                                        <span className="material-symbols-outlined text-[14px]">info</span>
                                                                        Información heredada de revisión anterior:
                                                                    </div>
                                                                    {hasInheritedComment && (
                                                                        <p className="italic mb-2 text-slate-700 dark:text-slate-300">"{initialItemState.comment}"</p>
                                                                    )}
                                                                    {hasInheritedPhotos && (
                                                                        <div className="flex flex-wrap gap-1.5">
                                                                            {initialItemState.photos.map((phUrl, phIdx) => (
                                                                                <a key={phIdx} href={phUrl} target="_blank" rel="noopener noreferrer" className="relative rounded overflow-hidden border border-slate-200 w-12 h-12 block hover:opacity-85 transition-opacity">
                                                                                    <img src={phUrl} alt="Heredada" className="w-full h-full object-cover" />
                                                                                </a>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })()}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>

                                {/* Signature Section */}
                                <div className="mt-12 mb-6">
                                    <div 
                                        className={`w-full max-w-md border border-slate-900 dark:border-slate-500 h-40 relative group bg-white dark:bg-slate-800 ${isEditable ? 'cursor-pointer' : 'cursor-not-allowed opacity-80'}`} 
                                        onClick={isEditable ? handleSign : undefined}
                                    >
                                        {currentSignature ? (
                                            <div className="absolute inset-0 flex items-center justify-center bg-white">
                                                {/* Represent signature, if it is a base64 image render it */}
                                                {currentSignature.startsWith('data:image') ? (
                                                    <img src={currentSignature} alt="Firma" className="max-h-full max-w-full object-contain mix-blend-multiply" />
                                                ) : (
                                                    <span className="italic font-bold text-blue-900" style={{ fontFamily: 'cursive' }}>Firma: {currentReviewerName}</span>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="absolute inset-0 flex items-center justify-center text-slate-300 group-hover:text-slate-400 select-none">
                                                Toque para dibujar su firma
                                            </div>
                                        )}
                                        <div className="absolute top-2 left-2 text-xs text-slate-500 bg-white/80 dark:bg-slate-800/80 px-1 rounded z-10">
                                            Firma {currentReviewerName}
                                        </div>
                                    </div>
                                    <div className="w-full max-w-md border-x border-b border-slate-900 dark:border-slate-500 p-2 text-center text-sm font-bold text-slate-700 dark:text-slate-300">
                                        {currentReviewerName}
                                    </div>
                                </div>

                                {/* Rejection & Resolution Block (NEW) */}
                                {isRejected && (() => {
                                    const lastRejectionEntry = getLastRejectionEntry(currentState.history);
                                    return (
                                        <div className="mt-8 border-t-2 border-red-100 pt-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                                            <h3 className="text-lg font-bold text-red-600 mb-4 flex items-center gap-2">
                                                <span className="material-symbols-outlined">warning</span>
                                                Protocolo Rechazado
                                            </h3>

                                            <div className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 rounded-lg p-4 mb-4">
                                                <h4 className="text-xs font-bold text-red-500 uppercase mb-2">Observaciones del Rechazo</h4>
                                                <p className="text-sm text-slate-700 dark:text-slate-300 italic mb-3">"{lastRejectionReason}"</p>
                                                {lastRejectionEntry?.images && lastRejectionEntry.images.length > 0 && (
                                                    <div className="mt-3">
                                                        <h5 className="text-[11px] font-bold text-red-400 uppercase mb-1.5 flex items-center gap-1"><span className="material-symbols-outlined text-[14px]">photo_library</span> Evidencia de Falla</h5>
                                                        <div className="flex flex-wrap gap-2">
                                                            {lastRejectionEntry.images.map((url, imgIdx) => (
                                                                <a key={imgIdx} href={url} target="_blank" rel="noopener noreferrer" className="relative rounded overflow-hidden border border-red-200 w-24 h-24 block hover:opacity-85 transition-opacity shadow-sm">
                                                                    <img src={url} alt="Evidencia de Rechazo" className="w-full h-full object-cover" />
                                                                </a>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div>
                                                <h4 className="text-sm font-bold text-slate-800 dark:text-white mb-2 flex items-center gap-1">
                                                    Resolución de Observación
                                                    <span className="text-red-500">*</span>
                                                </h4>
                                                <textarea
                                                    className="w-full h-32 p-3 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-sm focus:ring-2 focus:ring-primary outline-none resize-none"
                                                    placeholder="Describa cómo se resolvieron las observaciones para proceder..."
                                                    value={resolutionText}
                                                    onChange={(e) => setResolutionText(e.target.value)}
                                                    disabled={!isEditable}
                                                />
                                            </div>

                                            <div className="mt-4">
                                                <h4 className="text-sm font-bold text-slate-800 dark:text-white mb-2 flex items-center gap-1">
                                                    Evidencia de Reparación
                                                    <span className="text-red-500">*</span>
                                                </h4>
                                                <div className="flex flex-wrap gap-3 mb-3">
                                                    {resolutionPhotos.map((url, idx) => (
                                                        <div key={idx} className="relative group rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 w-24 h-24 shadow-sm">
                                                            <img src={url} alt="Reparación" className="w-full h-full object-cover" />
                                                            {isEditable && (
                                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                    <button 
                                                                        type="button"
                                                                        onClick={() => setResolutionPhotos(prev => prev.filter((_, i) => i !== idx))} 
                                                                        className="bg-red-500 text-white p-1 rounded-full hover:bg-red-600 shadow-sm flex items-center justify-center"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[16px]">delete</span>
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                    {isEditable && (
                                                        <label className="w-24 h-24 flex flex-col items-center justify-center border-2 border-dashed border-slate-300 hover:border-slate-400 dark:border-slate-700 dark:hover:border-slate-600 rounded-lg cursor-pointer text-slate-400 hover:text-slate-500 transition-colors bg-slate-50 dark:bg-slate-800/50 shadow-sm select-none">
                                                            <span className="material-symbols-outlined text-2xl">add_a_photo</span>
                                                            <span className="text-[10px] font-bold mt-1">Subir Foto</span>
                                                            <input 
                                                                type="file" 
                                                                accept="image/*" 
                                                                capture="environment" 
                                                                className="hidden" 
                                                                onChange={(e) => {
                                                                    if (e.target.files && e.target.files.length > 0) {
                                                                        const file = e.target.files[0];
                                                                        const newPhotoUrl = URL.createObjectURL(file);
                                                                        setResolutionPhotos(prev => [...prev, newPhotoUrl]);
                                                                    }
                                                                    e.target.value = '';
                                                                }}
                                                            />
                                                        </label>
                                                    )}
                                                </div>
                                                {resolutionPhotos.length === 0 && (
                                                    <p className="text-xs text-red-500 font-bold flex items-center gap-1 select-none animate-pulse">
                                                        <span className="material-symbols-outlined text-[14px]">info</span>
                                                        * Es obligatorio adjuntar al menos una fotografía de la reparación como evidencia.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()}

                            </div>

                            {/* Footer Actions */}
                            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-col md:flex-row gap-3 md:gap-0 justify-between items-center z-10 shrink-0">
                                <button
                                    onClick={() => setHistoryModalOpen(true)}
                                    className="flex items-center justify-center md:justify-start gap-1 md:gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 py-2 px-4 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors w-full md:w-auto order-2 md:order-1 border md:border-none border-slate-200 dark:border-slate-700"
                                >
                                    <span className="material-symbols-outlined">history</span>
                                    <span className="font-medium text-sm">Historial de Protocolo</span>
                                </button>

                                <div className="grid grid-cols-2 md:flex md:flex-row gap-2 md:gap-3 w-full md:w-auto order-1 md:order-2">
                                    {canReject && !isRejected && isEditable && (
                                        <button
                                            onClick={handleRejectClick}
                                            className={`px-3 py-2 md:px-6 md:py-2 rounded-lg bg-red-50 text-red-600 font-bold text-xs md:text-sm border border-red-200 hover:bg-red-100 transition-colors w-full text-center ${!hasPermission("cc_completar", "edit") ? "opacity-50 pointer-events-none" : ""}`}
                                        >
                                            Rechazar
                                        </button>
                                    )}
                                    <button
                                        onClick={handleCloseProtocolModal}
                                        className="px-3 py-2 md:px-4 md:py-2 text-slate-500 font-bold text-xs md:text-sm hover:text-slate-700 bg-slate-100 md:bg-transparent hover:bg-slate-200 rounded-lg transition-colors w-full text-center"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleSaveProtocol}
                                        disabled={!isEditable}
                                        className={`px-3 py-2 md:px-6 md:py-2 text-white font-bold text-xs md:text-sm rounded-lg shadow-sm transition-colors flex items-center justify-center gap-1 md:gap-2 w-full ${isEditable ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600/50 cursor-not-allowed'} ${!hasPermission("cc_completar", "edit") ? "opacity-50 pointer-events-none" : ""}`}
                                    >
                                        <span className="material-symbols-outlined text-[16px] md:text-[18px]">save</span>
                                        Guardar
                                    </button>
                                    {isProjectAdmin() && currentState.status !== 'Cerrado' && (
                                        <button
                                            onClick={handleAdminLiberate}
                                            disabled={!currentSignature}
                                            className={`px-3 py-2 md:px-6 md:py-2 text-white font-bold text-xs md:text-sm rounded-lg shadow-sm transition-all flex items-center justify-center gap-1 md:gap-2 w-full ${currentSignature ? 'bg-amber-600 hover:bg-amber-700' : 'bg-amber-600/50 cursor-not-allowed'} ${!hasPermission("cc_completar", "edit") ? "opacity-50 pointer-events-none" : ""}`}
                                        >
                                            <span className="material-symbols-outlined text-[18px]">gavel</span>
                                            Liberar (Admin)
                                        </button>
                                    )}
                                    <button
                                        onClick={handleSendProtocol}
                                        disabled={!canSend}
                                        className={`px-3 py-2 md:px-6 md:py-2 text-white font-bold text-xs md:text-sm rounded-lg shadow-sm transition-all flex items-center justify-center gap-1 md:gap-2 w-full ${canSend ? 'bg-green-600 hover:bg-green-700' : 'bg-green-600/50 cursor-not-allowed'} ${!canReject || isRejected ? 'col-span-2 md:col-span-1' : ''} ${!hasPermission("cc_completar", "edit") ? "opacity-50 pointer-events-none" : ""}`}
                                    >
                                        <span className="material-symbols-outlined text-[18px]">check_circle</span>
                                        {isLastReviewer ? 'Cerrar' : 'Enviar'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
            {photoModalState.isOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg border border-slate-200 dark:border-slate-800 p-6 flex flex-col max-h-[80vh]">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2"><span className="material-symbols-outlined text-blue-500">photo_library</span>Fotografías Adjuntas</h3>
                            <button onClick={() => setPhotoModalState({ ...photoModalState, isOpen: false })} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                        </div>

                        <div className="flex-1 overflow-y-auto mb-4">
                            {photoModalState.photos.length === 0 ? (
                                <div className="h-40 flex items-center justify-center border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg text-slate-400 flex-col gap-2">
                                    <span className="material-symbols-outlined text-4xl">no_photography</span>
                                    <span className="text-sm">Sin fotografías</span>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-3">
                                    {photoModalState.photos.map((url, idx) => {
                                        const isInherited = photoModalState.itemId && initialChecklist[photoModalState.itemId]?.photos?.includes(url);
                                        return (
                                            <div key={idx} className="relative group rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 h-32">
                                                <img src={url} alt="Evidencia" className="w-full h-full object-cover" />
                                                {!photoModalState.isReadOnly && !isInherited && (
                                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                        <button onClick={() => handleRemovePhoto(idx)} className="bg-red-500 text-white p-1 rounded-full hover:bg-red-600">
                                                            <span className="material-symbols-outlined text-[20px]">delete</span>
                                                        </button>
                                                    </div>
                                                )}
                                                {isInherited && (
                                                    <div className="absolute top-2 left-2 bg-slate-800/80 text-white text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5 select-none">
                                                        <span className="material-symbols-outlined text-[10px]">lock</span> Heredada
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-3">
                            <button onClick={() => setPhotoModalState({ ...photoModalState, isOpen: false })} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cerrar</button>
                            <input 
                                type="file" 
                                accept="image/*" 
                                capture="environment" 
                                id="cameraInput"
                                className="hidden" 
                                onChange={(e) => {
                                    if (e.target.files && e.target.files.length > 0 && photoModalState.itemId) {
                                        const file = e.target.files[0];
                                        const newPhotoUrl = URL.createObjectURL(file);
                                        const updatedPhotos = [...photoModalState.photos, newPhotoUrl];
                                        setPhotoModalState({ ...photoModalState, photos: updatedPhotos });
                                        setTempChecklist(prev => ({
                                            ...prev,
                                            [photoModalState.itemId!]: {
                                                ...(prev[photoModalState.itemId!] || { status: 'pending', comment: '' }),
                                                photos: updatedPhotos
                                            }
                                        }));
                                        e.target.value = '';
                                    }
                                }} 
                            />
                            {!photoModalState.isReadOnly && (
                                <button onClick={handleAddPhoto} className="px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm flex items-center gap-2">
                                    <span className="material-symbols-outlined text-[18px]">add_a_photo</span>
                                    Agregar Foto
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* ... (Rest of existing Modals: configModalOpen, assignResponsiblesTask, baseConfigModalOpen, infoModalState, commentModalState, rejectionModalOpen, historyModalOpen) ... */}
            {configModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm sm:p-4">
                    <div className="bg-white dark:bg-slate-900 sm:rounded-xl shadow-2xl w-full max-w-6xl sm:border border-slate-200 dark:border-slate-800 flex flex-col h-full sm:h-[90vh]">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white">Configuración de Protocolos</h3>
                            <button onClick={() => setConfigModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-900/50">
                            <table className="w-full text-left border-collapse bg-white dark:bg-slate-900">
                                <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800 shadow-sm">
                                    <tr>
                                        <th className="px-4 py-3 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700 w-24">Código</th>
                                        <th className="px-4 py-3 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700">Nombre</th>
                                        <th className="px-4 py-3 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700 w-32 text-center">¿Aplica Protocolo?</th>
                                        <th className="px-4 py-3 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700 w-32 text-center">Código Protocolo</th>
                                        <th className="px-4 py-3 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700 w-48 text-center">Nombre Protocolo</th>
                                        <th className="px-4 py-3 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700 w-48 text-center">Grupos / Responsables</th>
                                        <th className="px-4 py-3 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700 w-20 text-center">Base</th>
                                        <th className="px-4 py-3 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700 w-40 text-center">Biblioteca Protocolos</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                    {renderConfigRows(tasks)}
                                </tbody>
                            </table>
                        </div>
                        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3 bg-white dark:bg-slate-900 rounded-b-xl">
                            <button onClick={() => setConfigModalOpen(false)} className="px-6 py-2 text-sm font-bold text-white bg-primary hover:bg-blue-600 rounded-lg shadow-sm">Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
            {reviewerGroupModalOpen && activeGroupTaskId && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-4xl border border-slate-200 dark:border-slate-800 flex flex-col h-[85vh]">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white">Flujos de Responsables por Sector</h3>
                            <button onClick={() => setReviewerGroupModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto p-6 bg-slate-50 dark:bg-slate-900/50">
                            <div className="flex flex-col gap-6">
                                <button
                                    onClick={handleAddGroup}
                                    className="w-full py-3 border-2 border-dashed border-primary/30 text-primary font-bold rounded-lg hover:bg-primary/5 transition-colors flex items-center justify-center gap-2 bg-white dark:bg-slate-800"
                                >
                                    <span className="material-symbols-outlined">add_circle</span>
                                    Crear Grupo
                                </button>

                                {protocolConfigs[activeGroupTaskId]?.reviewerGroups?.map((group) => (
                                    <div key={group.id} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-in fade-in slide-in-from-top-2">
                                        <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                                            <div className="flex-1 max-w-sm">
                                                <input
                                                    type="text"
                                                    className="w-full bg-transparent font-bold text-slate-800 dark:text-white outline-none border-b border-transparent focus:border-primary px-1"
                                                    placeholder="Nombre del Grupo (ej: Sector 1)..."
                                                    value={group.name}
                                                    onChange={(e) => handleUpdateGroup(group.id, 'name', e.target.value)}
                                                />
                                            </div>
                                            <button onClick={() => handleRemoveGroup(group.id)} className="text-slate-400 hover:text-red-500 transition-colors">
                                                <span className="material-symbols-outlined text-[20px]">delete</span>
                                            </button>
                                        </div>

                                        <div className="p-6 flex flex-col gap-6">
                                            {/* Units Selection */}
                                            <div>
                                                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Unidades Físicas</h4>
                                                <button
                                                    onClick={() => { setActiveGroupId(group.id); setUnitSelectorOpen(true); }}
                                                    className="w-full py-2.5 px-4 bg-slate-100 dark:bg-slate-900 rounded-lg text-sm text-slate-700 dark:text-slate-300 font-medium flex items-center justify-between hover:bg-slate-200 transition-colors"
                                                >
                                                    <span>{group.unitIds.length} unidades seleccionadas</span>
                                                    <span className="material-symbols-outlined text-[18px]">domain</span>
                                                </button>
                                                <div className="mt-2 flex flex-wrap gap-1.5">
                                                    {group.unitIds.map(uid => (
                                                        <span key={uid} className="px-2 py-0.5 bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 text-[10px] font-bold rounded-full border border-blue-100 dark:border-blue-900/30">
                                                            {units.find(u => u.id === uid)?.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Reviewers List */}
                                            <div>
                                                <div className="flex items-center gap-4 mb-3">
                                                    <div className="flex-1 flex items-center gap-2">
                                                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Revisores</h4>
                                                        <button
                                                            onClick={() => handleAddReviewerToGroup(group.id)}
                                                            className="size-5 bg-green-500 text-white rounded flex items-center justify-center hover:bg-green-600 transition-colors shadow-sm"
                                                            title="Añadir Revisor"
                                                        >
                                                            <span className="material-symbols-outlined text-[16px] font-bold">add</span>
                                                        </button>
                                                    </div>
                                                    <div className="flex gap-4">
                                                        <span className="text-[10px] font-bold text-slate-400 uppercase w-24 text-center leading-tight">Genera<br/>Avance</span>
                                                        <span className="text-[10px] font-bold text-slate-400 uppercase w-24 text-center leading-tight">Genera<br/>Pago</span>
                                                    </div>
                                                </div>

                                                <div className="space-y-3">
                                                    {group.reviewers.map((rev, index) => (
                                                        <div key={index} className="flex items-center gap-4">
                                                            <div className="flex-1">
                                                                <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Revisor {index + 1}</label>
                                                                <select
                                                                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                                                                    value={rev.id}
                                                                    onChange={(e) => handleUpdateReviewerInGroup(group.id, index, 'id', e.target.value)}
                                                                >
                                                                    <option value="">Seleccionar...</option>
                                                                    {responsibles.map(r => <option key={r.id} value={r.id}>{r.name} ({r.role})</option>)}
                                                                </select>
                                                            </div>
                                                            <div className="flex items-center gap-4 pt-4">
                                                                <div className="w-24 flex justify-center">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="size-5 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                                                                        checked={rev.generatesProgress}
                                                                        onChange={(e) => handleUpdateReviewerInGroup(group.id, index, 'generatesProgress', e.target.checked)}
                                                                    />
                                                                </div>
                                                                <div className="w-24 flex justify-center">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="size-5 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                                                                        checked={rev.generatesPayment}
                                                                        onChange={(e) => handleUpdateReviewerInGroup(group.id, index, 'generatesPayment', e.target.checked)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {group.reviewers.length === 0 && (
                                                        <p className="text-sm text-slate-400 italic text-center py-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg">Pulse el botón +A para añadir revisores.</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end bg-white dark:bg-slate-900 rounded-b-xl">
                            <button onClick={() => setReviewerGroupModalOpen(false)} className="px-8 py-2 bg-primary text-white font-bold rounded-lg shadow-md hover:bg-blue-600 transition-colors">Listo</button>
                        </div>
                    </div>
                </div>
            )}

            {unitSelectorOpen && activeGroupId && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 flex flex-col h-[70vh]">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white">Seleccionar Unidades Físicas</h3>
                            <button onClick={() => setUnitSelectorOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto p-4">
                            <div className="space-y-4">
                                {levels.map(level => {
                                    const levelUnits = units.filter(u => u.levelId === level.id);
                                    const selectedCount = levelUnits.filter(u => protocolConfigs[activeGroupTaskId!]?.reviewerGroups?.find(g => g.id === activeGroupId)?.unitIds.includes(u.id)).length;
                                    const isAllSelected = selectedCount === levelUnits.length && levelUnits.length > 0;

                                    return (
                                        <div key={level.id} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-800">
                                            <div className="bg-slate-50 dark:bg-slate-800/50 p-3 flex items-center justify-between border-b border-slate-200 dark:border-slate-700">
                                                <div className="flex items-center gap-3">
                                                    <input
                                                        type="checkbox"
                                                        className="size-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                                                        checked={isAllSelected}
                                                        onChange={(e) => {
                                                            const currentIds = protocolConfigs[activeGroupTaskId!]?.reviewerGroups?.find(g => g.id === activeGroupId)?.unitIds || [];
                                                            let nextIds;
                                                            if (e.target.checked) {
                                                                // Add all units of this level that are not already there
                                                                const levelUnitIds = levelUnits.map(u => u.id);
                                                                nextIds = Array.from(new Set([...currentIds, ...levelUnitIds]));
                                                            } else {
                                                                // Remove all units of this level
                                                                const levelUnitIds = levelUnits.map(u => u.id);
                                                                nextIds = currentIds.filter(id => !levelUnitIds.includes(id));
                                                            }
                                                            handleUpdateGroup(activeGroupId, 'unitIds', nextIds);
                                                        }}
                                                    />
                                                    <span className="font-bold text-slate-800 dark:text-white">{level.name}</span>
                                                </div>
                                                <span className="text-xs text-slate-500">{selectedCount}/{levelUnits.length} seleccionadas</span>
                                            </div>
                                            <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                                                {levelUnits.map(unit => (
                                                    <label key={unit.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-md cursor-pointer transition-colors border border-transparent hover:border-slate-100">
                                                        <input
                                                            type="checkbox"
                                                            className="size-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                                                            checked={protocolConfigs[activeGroupTaskId!]?.reviewerGroups?.find(g => g.id === activeGroupId)?.unitIds.includes(unit.id)}
                                                            onChange={(e) => {
                                                                const currentIds = protocolConfigs[activeGroupTaskId!]?.reviewerGroups?.find(g => g.id === activeGroupId)?.unitIds || [];
                                                                const nextIds = e.target.checked
                                                                    ? [...currentIds, unit.id]
                                                                    : currentIds.filter(id => id !== unit.id);
                                                                handleUpdateGroup(activeGroupId, 'unitIds', nextIds);
                                                            }}
                                                        />
                                                        <span className="text-sm text-slate-700 dark:text-slate-300">{unit.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end bg-white dark:bg-slate-900 rounded-b-xl">
                            <button onClick={() => setUnitSelectorOpen(false)} className="px-6 py-2 bg-primary text-white font-bold rounded-lg shadow-md hover:bg-blue-600 transition-colors">Confirmar</button>
                        </div>
                    </div>
                </div>
            )}
            {baseConfigModalOpen && activeBaseConfig && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-5xl border border-slate-200 dark:border-slate-800 flex flex-col h-[80vh]">
                        {/* Header Info */}
                        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-xl font-bold text-slate-800 dark:text-white">Configuración de Protocolo Base</h3>
                                <button onClick={() => setBaseConfigModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-100 dark:border-slate-700">
                                <div><label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Proyecto</label><p className="text-sm font-medium text-slate-900 dark:text-white">{projectInfo?.name || 'Proyecto no definido'}</p></div>
                                <div><label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Tarea</label><p className="text-sm font-medium text-slate-900 dark:text-white">{activeBaseTaskName}</p></div>
                                <div><label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Código Protocolo</label><p className="text-sm font-medium text-slate-900 dark:text-white">{activeBaseConfig.code ? `${activeBaseConfig.code}-###` : 'S/C'}</p></div>
                                <div><label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Nombre Protocolo</label><p className="text-sm font-medium text-slate-900 dark:text-white">{activeBaseConfig.protocolName || 'Sin nombre'}</p></div>
                            </div>
                        </div>
                        {/* Checklist Body */}
                        <div className="flex-1 overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50">
                            <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                                <table className="w-full text-left border-collapse">
                                    <thead className="bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                                        <tr>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-12 text-center">#</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Parámetro de Revisión</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-64 text-center">Configuración</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-12"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {(activeBaseConfig.items || []).map((item, index) => (
                                            <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                                <td className="px-4 py-3 text-slate-500 font-mono text-sm w-12 text-center">{index + 1}</td>
                                                <td className="px-4 py-3"><input type="text" placeholder="Ingrese el parámetro a revisar..." className="w-full border-none bg-transparent focus:ring-0 text-sm placeholder-slate-400 text-slate-800 dark:text-slate-200 outline-none" value={item.text} onChange={(e) => handleUpdateBaseItem(item.id, e.target.value)} /></td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center justify-center gap-3">
                                                        <button onClick={() => handleOpenInfoModal(item.id)} className={`text-blue-500 hover:text-blue-600 transition-colors p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 relative group ${item.additionalInfo ? 'bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-200' : ''}`} title={item.additionalInfo || "Agregar información adicional"}><span className="material-symbols-outlined text-[20px]">info</span></button>
                                                        <span className="text-green-600 opacity-40 cursor-not-allowed"><span className="material-symbols-outlined text-[20px]">check_circle</span></span>
                                                        <span className="text-red-500 opacity-40 cursor-not-allowed"><span className="material-symbols-outlined text-[20px]">cancel</span></span>
                                                        <span className="text-slate-400 opacity-40 cursor-not-allowed"><span className="material-symbols-outlined text-[20px]">remove_circle</span></span>
                                                        <span className="text-slate-400 opacity-40 cursor-not-allowed"><span className="material-symbols-outlined text-[20px]">chat</span></span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-center"><button onClick={() => handleDeleteBaseItem(item.id)} className="text-slate-400 hover:text-red-500 transition-colors"><span className="material-symbols-outlined text-[18px]">delete</span></button></td>
                                            </tr>
                                        ))}
                                        {(activeBaseConfig.items || []).length === 0 && (
                                            <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400 italic text-sm">No hay parámetros definidos. Pulse "+ Añadir Parámetros" para comenzar.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-b-xl flex justify-between items-center">
                            <button onClick={handleAddBaseItem} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors"><span className="material-symbols-outlined text-[18px]">add</span>Añadir Parámetros</button>
                            <button onClick={() => setBaseConfigModalOpen(false)} className="px-6 py-2 bg-primary text-white rounded-lg text-sm font-bold hover:bg-blue-600 shadow-sm transition-colors">Guardar y Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
            {infoModalState.isOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg border border-slate-200 dark:border-slate-800 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2"><span className="material-symbols-outlined text-blue-500">info</span>Información Adicional</h3>
                            <button onClick={() => setInfoModalState({ ...infoModalState, isOpen: false })} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                        </div>
                        <div className="mb-4">
                            <textarea
                                className="w-full h-32 p-3 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-primary outline-none resize-none"
                                placeholder={infoModalState.isReadOnly ? "No hay información adicional." : "Escriba aquí los detalles..."}
                                value={infoModalState.text}
                                onChange={(e) => !infoModalState.isReadOnly && setInfoModalState({ ...infoModalState, text: e.target.value })}
                                readOnly={infoModalState.isReadOnly}
                                autoFocus={!infoModalState.isReadOnly}
                            />
                        </div>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setInfoModalState({ ...infoModalState, isOpen: false })} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cerrar</button>
                            {!infoModalState.isReadOnly && <button onClick={handleSaveInfo} className="px-6 py-2 text-sm font-bold text-white bg-primary hover:bg-blue-600 rounded-lg shadow-sm">Guardar Información</button>}
                        </div>
                    </div>
                </div>
            )}
            {commentModalState.isOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg border border-slate-200 dark:border-slate-800 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2"><span className="material-symbols-outlined text-amber-500">chat_bubble</span>Comentarios / Observaciones</h3>
                            <button onClick={() => setCommentModalState({ ...commentModalState, isOpen: false })} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                        </div>
                        <div className="mb-4">
                            <textarea
                                className="w-full h-32 p-3 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-primary outline-none resize-none"
                                placeholder="Escriba aquí sus observaciones..."
                                value={commentModalState.text}
                                onChange={(e) => setCommentModalState({ ...commentModalState, text: e.target.value })}
                                disabled={commentModalState.isReadOnly}
                                autoFocus={!commentModalState.isReadOnly}
                            />
                        </div>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setCommentModalState({ ...commentModalState, isOpen: false })} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
                                {commentModalState.isReadOnly ? 'Cerrar' : 'Cancelar'}
                            </button>
                            {!commentModalState.isReadOnly && (
                                <button onClick={handleSaveComment} className="px-6 py-2 text-sm font-bold text-white bg-primary hover:bg-blue-600 rounded-lg shadow-sm">
                                    Guardar Comentario
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {rejectionModalOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg border border-slate-200 dark:border-slate-800 p-6 flex flex-col max-h-[85vh]">
                        <div className="flex items-center justify-between mb-4 shrink-0">
                            <h3 className="text-lg font-bold text-red-600 flex items-center gap-2"><span className="material-symbols-outlined">warning</span>Rechazar Protocolo</h3>
                            <button onClick={() => setRejectionModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                        </div>
                        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
                            <p className="text-sm text-slate-600 dark:text-slate-300">Indique el motivo del rechazo. El protocolo será devuelto al revisor anterior.</p>
                            <textarea
                                className="w-full h-32 p-3 border border-red-200 rounded-lg bg-red-50 text-slate-900 text-sm focus:ring-2 focus:ring-red-500 outline-none resize-none"
                                placeholder="Motivo del rechazo..."
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                autoFocus
                            />
                            
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Evidencia Fotográfica (Opcional)</label>
                                {rejectionPhotos.length > 0 && (
                                    <div className="grid grid-cols-3 gap-2 mb-3">
                                        {rejectionPhotos.map((url, idx) => (
                                            <div key={idx} className="relative group rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 h-20">
                                                <img src={url} alt="Evidencia Rechazo" className="w-full h-full object-cover" />
                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                    <button 
                                                        onClick={() => setRejectionPhotos(prev => prev.filter((_, i) => i !== idx))} 
                                                        className="bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                                                    >
                                                        <span className="material-symbols-outlined text-[16px]">delete</span>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <input 
                                    type="file" 
                                    accept="image/*" 
                                    capture="environment" 
                                    id="rejectionCameraInput"
                                    className="hidden" 
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files.length > 0) {
                                            const file = e.target.files[0];
                                            const newPhotoUrl = URL.createObjectURL(file);
                                            setRejectionPhotos(prev => [...prev, newPhotoUrl]);
                                            e.target.value = '';
                                        }
                                    }} 
                                />
                                <button 
                                    onClick={() => document.getElementById('rejectionCameraInput')?.click()} 
                                    className="px-3 py-1.5 text-xs font-bold text-slate-700 dark:text-slate-300 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-lg flex items-center gap-1.5 transition-colors border border-slate-200 dark:border-slate-700"
                                >
                                    <span className="material-symbols-outlined text-[16px]">add_a_photo</span>
                                    Adjuntar Evidencia
                                </button>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-4 shrink-0 border-t border-slate-100 dark:border-slate-800 pt-3">
                            <button onClick={() => setRejectionModalOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
                            <button onClick={confirmRejection} disabled={!rejectionReason.trim()} className="px-6 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-sm disabled:opacity-50">Confirmar Rechazo</button>
                        </div>
                    </div>
                </div>
            )}
            {historyModalOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg border border-slate-200 dark:border-slate-800 flex flex-col max-h-[70vh]">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2"><span className="material-symbols-outlined text-slate-500">history</span>Historial de Protocolo</h3>
                            <button onClick={() => setHistoryModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {getCurrentState().history.length === 0 ? (
                                <p className="text-center text-slate-400 italic">No hay historial registrado.</p>
                            ) : (
                                getCurrentState().history.map((entry, index) => (
                                    <div 
                                        key={index} 
                                        onClick={() => setSelectedSnapshotEntry(entry)}
                                        className="flex gap-3 text-sm p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer transition-all border border-transparent hover:border-slate-150 dark:hover:border-slate-800/60 shadow-sm"
                                        title="Haga clic para ver la captura exacta de este paso"
                                    >
                                        <div className={`mt-1 size-2.5 rounded-full shrink-0 ${
                                            entry.action === 'rejected' ? 'bg-red-500 shadow shadow-red-500/50' :
                                            entry.action === 'closed' ? 'bg-green-600 shadow shadow-green-600/50' :
                                            entry.action === 'resolved' ? 'bg-orange-500 shadow shadow-orange-500/50' :
                                            entry.action === 'approved' ? 'bg-blue-500 shadow shadow-blue-500/50' : 'bg-slate-400'
                                        }`}></div>
                                        <div className="flex-1">
                                            <div className="flex justify-between items-start gap-2">
                                                <p className="font-bold text-slate-900 dark:text-white leading-tight">
                                                    {entry.action === 'created' ? 'Creado' :
                                                        entry.action === 'saved' ? 'Guardado' :
                                                            entry.action === 'approved' ? 'Aprobado/Enviado' :
                                                                entry.action === 'rejected' ? 'Rechazado' :
                                                                    entry.action === 'resolved' ? 'Resuelto' :
                                                                        entry.action === 'closed' ? 'Cerrado' : entry.action}
                                                </p>
                                                <span className="text-[10px] font-bold text-slate-400 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded flex items-center gap-1 shrink-0 select-none">
                                                    <span className="material-symbols-outlined text-[12px]">photo_camera</span> Ver captura
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-500 mt-0.5">{entry.date} por {entry.user}</p>
                                            {entry.comment && <p className="text-xs text-slate-600 dark:text-slate-300 mt-1.5 italic bg-slate-50 dark:bg-slate-800/30 p-1.5 rounded border border-slate-100 dark:border-slate-800/50">"{entry.comment}"</p>}
                                            {entry.images && entry.images.length > 0 && (
                                                <div className="grid grid-cols-3 gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                                                    {entry.images.map((url, imgIdx) => (
                                                        <img 
                                                            key={imgIdx} 
                                                            src={url} 
                                                            alt="Evidencia" 
                                                            className="w-full h-16 object-cover rounded border border-slate-200 dark:border-slate-700 cursor-pointer hover:opacity-85"
                                                            onClick={() => window.open(url, '_blank')} 
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {selectedSnapshotEntry && (() => {
                const config = getActiveConfig();
                const items = config?.items || [];
                const snapshotData = selectedSnapshotEntry.snapshot || {};
                
                return (
                    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 flex flex-col h-[80vh] overflow-hidden">
                            {/* Header */}
                            <div className="p-4 md:p-6 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex justify-between items-center shrink-0">
                                <div>
                                    <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                        <span className="material-symbols-outlined text-blue-600">photo_camera</span>
                                        Captura Instantánea Histórica
                                    </h3>
                                    <p className="text-xs text-slate-500 mt-1">
                                        Estado en el momento de: <span className="font-bold text-slate-700 dark:text-slate-300">
                                            {selectedSnapshotEntry.action === 'created' ? 'Creado' :
                                             selectedSnapshotEntry.action === 'saved' ? 'Guardado' :
                                             selectedSnapshotEntry.action === 'approved' ? 'Aprobado/Enviado' :
                                             selectedSnapshotEntry.action === 'rejected' ? 'Rechazado' :
                                             selectedSnapshotEntry.action === 'resolved' ? 'Resuelto' :
                                             selectedSnapshotEntry.action === 'closed' ? 'Cerrado' : selectedSnapshotEntry.action}
                                        </span> por <span className="font-bold text-slate-700 dark:text-slate-300">{selectedSnapshotEntry.user}</span> el {selectedSnapshotEntry.date}
                                    </p>
                                </div>
                                <button 
                                    onClick={() => setSelectedSnapshotEntry(null)} 
                                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                                >
                                    <span className="material-symbols-outlined text-[24px]">close</span>
                                </button>
                            </div>

                            {/* Body */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                                {/* Event comment and photos */}
                                {(selectedSnapshotEntry.comment || (selectedSnapshotEntry.images && selectedSnapshotEntry.images.length > 0)) && (
                                    <div className="p-4 rounded-lg border bg-slate-50 dark:bg-slate-800/30 border-slate-200 dark:border-slate-700/50">
                                        <h4 className="text-xs font-bold text-slate-400 uppercase mb-2">Comentario / Evidencia del Evento</h4>
                                        {selectedSnapshotEntry.comment && (
                                            <p className="text-sm text-slate-700 dark:text-slate-300 italic mb-3">
                                                "{selectedSnapshotEntry.comment}"
                                            </p>
                                        )}
                                        {selectedSnapshotEntry.images && selectedSnapshotEntry.images.length > 0 && (
                                            <div className="flex flex-wrap gap-2">
                                                {selectedSnapshotEntry.images.map((imgUrl, imgIdx) => (
                                                    <a key={imgIdx} href={imgUrl} target="_blank" rel="noopener noreferrer" className="relative rounded overflow-hidden border border-slate-200 w-20 h-20 block hover:opacity-85 transition-opacity">
                                                        <img src={imgUrl} alt="Evidencia" className="w-full h-full object-cover" />
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Checklist Parameters */}
                                <div>
                                    <h4 className="text-xs font-bold text-slate-400 uppercase mb-3">Parámetros del Protocolo</h4>
                                    {Object.keys(snapshotData).length === 0 ? (
                                        <p className="text-center text-slate-400 italic py-6">Datos de captura no disponibles para este evento histórico.</p>
                                    ) : (
                                        <div className="space-y-4">
                                            {items.map((item, idx) => {
                                                const val = snapshotData[item.id] || { status: 'pending', comment: '', photos: [] };
                                                return (
                                                    <div key={item.id} className="pb-3 border-b border-slate-100 dark:border-slate-800 last:border-0">
                                                        <div className="flex items-start justify-between gap-3">
                                                            <div className="flex gap-2">
                                                                <span className="font-bold text-slate-400 text-sm mt-0.5">{idx + 1}.</span>
                                                                <span className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">{item.text}</span>
                                                            </div>
                                                            <div className="shrink-0">
                                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold text-white shadow-sm ${
                                                                    val.status === 'pass' ? 'bg-green-500' :
                                                                    val.status === 'fail' ? 'bg-red-500' :
                                                                    val.status === 'na' ? 'bg-slate-500' : 'bg-slate-300 text-slate-600'
                                                                }`}>
                                                                    {val.status === 'pass' ? 'Cumple' :
                                                                     val.status === 'fail' ? 'No Cumple' :
                                                                     val.status === 'na' ? 'N/A' : 'Pendiente'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        {(val.comment || (val.photos && val.photos.length > 0)) && (
                                                            <div className="mt-2 pl-6 space-y-2 bg-slate-50/50 dark:bg-slate-800/20 p-2 rounded border border-slate-100 dark:border-slate-800/50">
                                                                {val.comment && (
                                                                    <p className="text-xs text-slate-600 dark:text-slate-400 italic">
                                                                        <span className="font-bold not-italic text-slate-400">Comentario:</span> "{val.comment}"
                                                                    </p>
                                                                )}
                                                                {val.photos && val.photos.length > 0 && (
                                                                    <div className="flex flex-wrap gap-1.5">
                                                                        {val.photos.map((phUrl, phIdx) => (
                                                                            <a key={phIdx} href={phUrl} target="_blank" rel="noopener noreferrer" className="relative rounded overflow-hidden border border-slate-200 w-12 h-12 block hover:opacity-85 transition-opacity">
                                                                                <img src={phUrl} alt="Foto item" className="w-full h-full object-cover" />
                                                                            </a>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end bg-slate-50 dark:bg-slate-800/30 shrink-0">
                                <button 
                                    onClick={() => setSelectedSnapshotEntry(null)} 
                                    className="px-5 py-2 text-sm font-bold text-white bg-slate-600 hover:bg-slate-700 rounded-lg shadow-sm"
                                >
                                    Cerrar Captura
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {exportModalOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[85vh]">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                <span className="material-symbols-outlined text-blue-600">download</span>
                                Exportar Protocolos a PDF
                            </h3>
                            <button onClick={() => setExportModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                        </div>

                        <div className="p-6 overflow-y-auto space-y-6">
                            {/* Step 1: Select Partida */}
                            <div>
                                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">1. Seleccionar Partida</label>
                                <select
                                    className="w-full p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                                    value={selectedExportTaskId || ''}
                                    onChange={(e) => {
                                        setSelectedExportTaskId(e.target.value);
                                        setSelectedProtocolIds(new Set());
                                    }}
                                >
                                    <option value="">Seleccione una partida...</option>
                                    {tasksWithProtocols.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Step 2: Select Protocols */}
                            {selectedExportTaskId && (
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">2. Seleccionar Protocolos ({protocolsForExport.length} encontrados)</label>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setSelectedProtocolIds(new Set(protocolsForExport.map(p => p.id)))}
                                                className="text-xs text-blue-600 hover:underline font-medium"
                                            >
                                                Seleccionar Todos
                                            </button>
                                            <span className="text-slate-300">|</span>
                                            <button
                                                onClick={() => setSelectedProtocolIds(new Set())}
                                                className="text-xs text-slate-500 hover:underline font-medium"
                                            >
                                                Ninguno
                                            </button>
                                        </div>
                                    </div>

                                    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                                        <table className="w-full text-sm">
                                            <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                                                <tr>
                                                    <th className="px-4 py-2 text-left font-medium w-10"></th>
                                                    <th className="px-4 py-2 text-left font-medium">Folio</th>
                                                    <th className="px-4 py-2 text-left font-medium">Ubicación</th>
                                                    <th className="px-4 py-2 text-left font-medium">Estado</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                                {protocolsForExport.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={4} className="px-4 py-8 text-center text-slate-400 italic">No hay protocolos completados para esta partida.</td>
                                                    </tr>
                                                ) : (
                                                    protocolsForExport.map(p => (
                                                        <tr
                                                            key={p.id}
                                                            className={`hover:bg-blue-50/50 dark:hover:bg-blue-900/20 cursor-pointer transition-colors ${selectedProtocolIds.has(p.id) ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                                                            onClick={() => {
                                                                const newIds = new Set(selectedProtocolIds);
                                                                if (newIds.has(p.id)) newIds.delete(p.id);
                                                                else newIds.add(p.id);
                                                                setSelectedProtocolIds(newIds);
                                                            }}
                                                        >
                                                            <td className="px-4 py-2">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedProtocolIds.has(p.id)}
                                                                    onChange={() => { }} // Handled by tr onClick
                                                                    className="rounded text-blue-600 focus:ring-blue-500"
                                                                />
                                                            </td>
                                                            <td className="px-4 py-2 font-mono text-xs">{p.id}</td>
                                                            <td className="px-4 py-2 text-xs">{p.levelName} - {p.unitName}</td>
                                                            <td className="px-4 py-2">
                                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${p.status === 'Cerrado' ? 'bg-green-100 text-green-700' :
                                                                    p.status === 'Rechazado' ? 'bg-red-100 text-red-700' :
                                                                        'bg-blue-100 text-blue-700'
                                                                    }`}>
                                                                    {p.status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3 bg-slate-50 dark:bg-slate-800/50">
                            <button onClick={() => setExportModalOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors">Cancelar</button>
                            <button
                                onClick={handleExportSelected}
                                disabled={selectedProtocolIds.size === 0}
                                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-bold rounded-lg shadow-md transition-all flex items-center gap-2"
                            >
                                <span className="material-symbols-outlined text-[20px]">file_download</span>
                                Exportar {selectedProtocolIds.size > 0 ? `(${selectedProtocolIds.size})` : ''}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {importMasterModalOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-4xl border border-slate-200 dark:border-slate-800 flex flex-col h-[75vh]">
                        {/* Header */}
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50 shrink-0">
                            <div>
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-green-600">fact_check</span>
                                    Biblioteca de Protocolos
                                </h3>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {importModalTab === 'maestro' 
                                        ? 'Seleccione una plantilla estándar para asociar a esta partida.' 
                                        : 'Seleccione un protocolo existente en el proyecto para duplicar su configuración.'}
                                </p>
                            </div>
                            <button onClick={() => setImportMasterModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        {/* Tabs */}
                        <div className="flex border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/20 px-4 pt-2 shrink-0">
                            <button
                                onClick={() => setImportModalTab('maestro')}
                                className={`px-4 py-2.5 font-bold text-xs uppercase tracking-wider border-b-2 transition-all flex items-center gap-2 ${
                                    importModalTab === 'maestro'
                                        ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                }`}
                            >
                                <span className="material-symbols-outlined text-[18px]">verified</span>
                                Maestro de Protocolos
                            </button>
                            <button
                                onClick={() => setImportModalTab('proyecto')}
                                className={`px-4 py-2.5 font-bold text-xs uppercase tracking-wider border-b-2 transition-all flex items-center gap-2 ${
                                    importModalTab === 'proyecto'
                                        ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                }`}
                            >
                                <span className="material-symbols-outlined text-[18px]">domain</span>
                                Proyecto
                            </button>
                        </div>

                        {/* Search and Filters */}
                        <div className="p-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
                            <div className="relative">
                                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-[20px]">search</span>
                                <input
                                    type="text"
                                    placeholder={importModalTab === 'maestro' 
                                        ? "Buscar plantillas por nombre, código, categoría o subcategoría..." 
                                        : "Buscar protocolos por partida, nombre o código..."}
                                    value={importSearch}
                                    onChange={(e) => setImportSearch(e.target.value)}
                                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg pl-10 pr-4 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white"
                                />
                            </div>
                        </div>

                        {/* Content Area */}
                        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/30 dark:bg-slate-900/10">
                            {importModalTab === 'maestro' ? (
                                <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-950 shadow-sm">
                                    <table className="w-full text-sm text-left border-collapse">
                                        <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-xs uppercase font-bold border-b border-slate-200 dark:border-slate-800">
                                            <tr>
                                                <th className="px-4 py-3">Nombre del Protocolo</th>
                                                <th className="px-4 py-3 w-28">Código</th>
                                                <th className="px-4 py-3 w-40">Categoría</th>
                                                <th className="px-4 py-3 w-40">Subcategoría</th>
                                                <th className="px-4 py-3 w-24 text-center">Parámetros</th>
                                                <th className="px-4 py-3 w-24 text-center">Acción</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-700 dark:text-slate-300">
                                            {globalProtocolTemplates
                                                .filter(t => {
                                                    const search = importSearch.toLowerCase();
                                                    return !search ||
                                                        t.protocolName.toLowerCase().includes(search) ||
                                                        (t.code || '').toLowerCase().includes(search) ||
                                                        (t.category || '').toLowerCase().includes(search) ||
                                                        (t.subcategory || '').toLowerCase().includes(search);
                                                })
                                                .map(template => (
                                                    <tr 
                                                        key={template.id} 
                                                        className="hover:bg-slate-50 dark:hover:bg-slate-900/40 cursor-pointer select-none"
                                                        onDoubleClick={() => {
                                                            setSelectedPreviewTemplate(template);
                                                            setPreviewModalOpen(true);
                                                        }}
                                                        title="Doble clic para previsualizar parámetros"
                                                    >
                                                        <td className="px-4 py-3 font-semibold text-slate-850 dark:text-slate-200">{template.protocolName}</td>
                                                        <td className="px-4 py-3 font-mono text-xs">{template.code || '-'}</td>
                                                        <td className="px-4 py-3 text-xs">
                                                            {template.category ? (
                                                                <span className={`px-2 py-0.5 rounded-full font-bold ${
                                                                    template.category === 'Obra Gruesa' ? 'bg-orange-50 text-orange-700 dark:bg-orange-950/20 dark:text-orange-400' :
                                                                    template.category === 'Terminaciones' ? 'bg-purple-50 text-purple-700 dark:bg-purple-950/20 dark:text-purple-400' :
                                                                    'bg-teal-50 text-teal-700 dark:bg-teal-950/20 dark:text-teal-400'
                                                                }`}>
                                                                    {template.category}
                                                                </span>
                                                            ) : '-'}
                                                        </td>
                                                        <td className="px-4 py-3 text-xs">{template.subcategory || '-'}</td>
                                                        <td className="px-4 py-3 text-center font-mono text-xs">{template.items?.length || 0}</td>
                                                        <td className="px-4 py-3 text-center">
                                                            <button
                                                                onClick={() => handleImportFromMaster(template)}
                                                                className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-bold transition-colors shadow-sm flex items-center gap-1 mx-auto"
                                                            >
                                                                <span className="material-symbols-outlined text-[14px]">download</span>
                                                                Importar
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            {globalProtocolTemplates.filter(t => {
                                                const search = importSearch.toLowerCase();
                                                return !search ||
                                                    t.protocolName.toLowerCase().includes(search) ||
                                                    (t.code || '').toLowerCase().includes(search) ||
                                                    (t.category || '').toLowerCase().includes(search) ||
                                                    (t.subcategory || '').toLowerCase().includes(search);
                                            }).length === 0 && (
                                                <tr>
                                                    <td colSpan={6} className="px-4 py-8 text-center text-slate-400 italic">No se encontraron plantillas en el maestro.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-955 shadow-sm">
                                    <table className="w-full text-sm text-left border-collapse">
                                        <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-xs uppercase font-bold border-b border-slate-200 dark:border-slate-800">
                                            <tr>
                                                <th className="px-4 py-3">Partida</th>
                                                <th className="px-4 py-3">Nombre del Protocolo</th>
                                                <th className="px-4 py-3 w-28">Código</th>
                                                <th className="px-4 py-3 w-28 text-center">Parámetros</th>
                                                <th className="px-4 py-3 w-36 text-center">Grupos / Resp.</th>
                                                <th className="px-4 py-3 w-28 text-center">Acción</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-700 dark:text-slate-300">
                                            {getProjectProtocols()
                                                .filter(item => item.task.id !== activeImportTaskId)
                                                .filter(item => {
                                                    const search = importSearch.toLowerCase();
                                                    return !search ||
                                                        item.task.name.toLowerCase().includes(search) ||
                                                        (item.config.protocolName || '').toLowerCase().includes(search) ||
                                                        (item.config.code || '').toLowerCase().includes(search);
                                                })
                                                .map(item => (
                                                    <tr 
                                                        key={item.task.id} 
                                                        className="hover:bg-slate-50 dark:hover:bg-slate-900/40 cursor-pointer select-none"
                                                        onDoubleClick={() => {
                                                            const previewTemplate: MasterProtocol = {
                                                                id: item.config.taskId,
                                                                protocolName: item.config.protocolName || 'Sin Nombre',
                                                                code: item.config.code,
                                                                category: item.config.category,
                                                                subcategory: item.config.subcategory,
                                                                items: item.config.items
                                                            };
                                                            setSelectedPreviewTemplate(previewTemplate);
                                                            setPreviewModalOpen(true);
                                                        }}
                                                        title="Doble clic para previsualizar parámetros"
                                                    >
                                                        <td className="px-4 py-3 font-semibold text-slate-800 dark:text-slate-250">{item.task.name}</td>
                                                        <td className="px-4 py-3 text-slate-700 dark:text-slate-350">{item.config.protocolName || 'Sin Nombre'}</td>
                                                        <td className="px-4 py-3 font-mono text-xs">{item.config.code || '-'}</td>
                                                        <td className="px-4 py-3 text-center font-mono text-xs">{item.config.items?.length || 0}</td>
                                                        <td className="px-4 py-3 text-center font-mono text-xs">{item.config.reviewerGroups?.length || 0}</td>
                                                        <td className="px-4 py-3 text-center">
                                                            <button
                                                                onClick={() => handleDuplicateProjectProtocol(item.config)}
                                                                className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs font-bold transition-colors shadow-sm flex items-center gap-1 mx-auto"
                                                            >
                                                                <span className="material-symbols-outlined text-[14px]">content_copy</span>
                                                                Duplicar
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            {getProjectProtocols()
                                                .filter(item => item.task.id !== activeImportTaskId)
                                                .filter(item => {
                                                    const search = importSearch.toLowerCase();
                                                    return !search ||
                                                        item.task.name.toLowerCase().includes(search) ||
                                                        (item.config.protocolName || '').toLowerCase().includes(search) ||
                                                        (item.config.code || '').toLowerCase().includes(search);
                                                }).length === 0 && (
                                                    <tr>
                                                        <td colSpan={6} className="px-4 py-8 text-center text-slate-400 italic">No se encontraron protocolos configurados en este proyecto.</td>
                                                    </tr>
                                                )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-3 bg-slate-50 dark:bg-slate-800/50 shrink-0">
                            <button onClick={() => setImportMasterModalOpen(false)} className="px-5 py-2 text-sm font-bold text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors">
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {previewModalOpen && selectedPreviewTemplate && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[80vh] animate-fade-in-up">
                        {/* Header */}
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
                            <div>
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-blue-600">visibility</span>
                                    Vista Previa: {selectedPreviewTemplate.protocolName}
                                </h3>
                                <p className="text-xs text-slate-500 mt-0.5 font-medium">Visualización de parámetros antes de importar.</p>
                            </div>
                            <button 
                                onClick={() => {
                                    setPreviewModalOpen(false);
                                    setSelectedPreviewTemplate(null);
                                }} 
                                className="text-slate-400 hover:text-slate-600"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        {/* Details */}
                        <div className="px-6 py-4 bg-slate-50/50 dark:bg-slate-800/20 border-b border-slate-100 dark:border-slate-800 grid grid-cols-3 gap-4 text-xs">
                            <div>
                                <span className="block font-bold text-slate-400 uppercase tracking-wider">Código</span>
                                <span className="font-mono text-sm font-semibold text-slate-700 dark:text-slate-350">{selectedPreviewTemplate.code || '-'}</span>
                            </div>
                            <div>
                                <span className="block font-bold text-slate-400 uppercase tracking-wider">Categoría</span>
                                {selectedPreviewTemplate.category ? (
                                    <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full font-bold ${
                                        selectedPreviewTemplate.category === 'Obra Gruesa' ? 'bg-orange-50 text-orange-700 dark:bg-orange-950/20 dark:text-orange-400' :
                                        selectedPreviewTemplate.category === 'Terminaciones' ? 'bg-purple-50 text-purple-700 dark:bg-purple-950/20 dark:text-purple-400' :
                                        'bg-teal-50 text-teal-700 dark:bg-teal-950/20 dark:text-teal-400'
                                    }`}>
                                        {selectedPreviewTemplate.category}
                                    </span>
                                ) : '-'}
                            </div>
                            <div>
                                <span className="block font-bold text-slate-400 uppercase tracking-wider">Subcategoría</span>
                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">{selectedPreviewTemplate.subcategory || '-'}</span>
                            </div>
                        </div>

                        {/* Parameters list */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                Parámetros de Control ({selectedPreviewTemplate.items?.length || 0})
                            </h4>
                            
                            <div className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden bg-white dark:bg-slate-950">
                                <table className="w-full text-sm text-left border-collapse">
                                    <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-xs uppercase font-bold border-b border-slate-200 dark:border-slate-800">
                                        <tr>
                                            <th className="px-4 py-2 w-12 text-center">#</th>
                                            <th className="px-4 py-2">Parámetro de Revisión</th>
                                            <th className="px-4 py-2">Información Adicional / Criterio</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-700 dark:text-slate-350">
                                        {(selectedPreviewTemplate.items || []).map((item, idx) => (
                                            <tr key={item.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-900/20">
                                                <td className="px-4 py-3 text-center font-mono text-xs text-slate-400">{idx + 1}</td>
                                                <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-200">{item.text}</td>
                                                <td className="px-4 py-3 text-xs italic text-slate-500 dark:text-slate-400">{item.additionalInfo || '-'}</td>
                                            </tr>
                                        ))}
                                        {(selectedPreviewTemplate.items || []).length === 0 && (
                                            <tr>
                                                <td colSpan={3} className="px-4 py-8 text-center text-slate-400 italic">Este protocolo no contiene parámetros configurados.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-3 bg-slate-50 dark:bg-slate-800/50">
                            <button 
                                onClick={() => {
                                    setPreviewModalOpen(false);
                                    setSelectedPreviewTemplate(null);
                                }} 
                                className="px-5 py-2 text-sm font-bold text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                            >
                                Cerrar
                            </button>
                            <button
                                onClick={() => {
                                    handleImportFromMaster(selectedPreviewTemplate);
                                    setPreviewModalOpen(false);
                                    setSelectedPreviewTemplate(null);
                                }}
                                className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-bold rounded-lg shadow-sm flex items-center gap-1.5 transition-colors"
                            >
                                <span className="material-symbols-outlined text-[18px]">download</span>
                                Importar este Protocolo
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <SignatureModal 
                isOpen={signatureModalOpen} 
                onClose={() => setSignatureModalOpen(false)} 
                onSave={(data) => setCurrentSignature(data)} 
            />
        </div>
    );
};

export default QualityScreen;